import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  ArrowLeft,
  ArrowRight,
  Building2,
  Check,
  ChevronDown,
  Clock,
  Bell,
  ListOrdered,
  Eye,
  EyeOff,
  HardHat,
  Sparkles,
  Upload,
  Wrench,
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import LanguageSelector from '../components/LanguageSelector';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import ProfilePhotoUpload from '../components/ProfilePhotoUpload';
import { saveProfilePhoto } from '../utils/profilePhotoStorage';
import { uploadRegistrationProfilePhoto } from '../services/profilePhotoApi';
import { exitToMarketingSite } from '../utils/marketingSiteExit';
import REGISTER_I18N_REGIONAL from '../i18n/registerI18nRegional';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';
const NAVY = '#0D1B2A';
const GOLD = '#C9A84C';
const CLIENT_TYPE_TIER1 = [
  { value: 'individual_small', icon: '🏠', labelKey: 'regTier1Individual' },
  { value: 'commercial_project', icon: '🏗', labelKey: 'regTier1Commercial' },
  { value: 'government_msme', icon: '🏛', labelKey: 'regTier1Government' },
];
const CLIENT_TYPE_TIER2 = {
  individual_small: [
    { value: 'individual_residential', icon: '🏡', labelKey: 'regSub_individualResidential' },
    { value: 'farmer', icon: '🌾', labelKey: 'regSub_farmer' },
    { value: 'well_digging', icon: '💧', labelKey: 'regSub_wellDigging' },
  ],
  commercial_project: [
    { value: 'industrial', icon: '🏭', labelKey: 'regSub_industrial' },
    { value: 'road_construction', icon: '🛣', labelKey: 'regSub_roadConstruction' },
    { value: 'canal_irrigation', icon: '🌊', labelKey: 'regSub_canalIrrigation' },
    { value: 'railway', icon: '🛤', labelKey: 'regSub_railway' },
    { value: 'rera_builder', icon: '🏢', labelKey: 'regSub_reraBuilder' },
  ],
  government_msme: [
    { value: 'pwd_government', icon: '🏛', labelKey: 'regSub_pwdGovernment' },
    { value: 'msme_commercial', icon: '🏪', labelKey: 'regSub_msmeCommercial' },
  ],
};
const OWNER_ENTITY_TIER1 = [
  { value: 'individual_individual', icon: '🏠', labelKey: 'regOwnerTier1Individual' },
  { value: 'business_registered', icon: '🏢', labelKey: 'regOwnerTier1Business' },
  { value: 'business_corporate', icon: '🏭', labelKey: 'regOwnerTier1Corporate' },
];
const OWNER_ENTITY_TIER2 = {
  individual_individual: [
    { value: 'individual', icon: '🧑', labelKey: 'regOwnerSub_individual' },
    { value: 'sole_proprietor', icon: '🏪', labelKey: 'regOwnerSub_soleProprietor' },
  ],
  business_registered: [
    { value: 'partnership', icon: '👥', labelKey: 'regOwnerSub_partnership' },
    { value: 'llp', icon: '🏢', labelKey: 'regOwnerSub_llp' },
  ],
  business_corporate: [
    { value: 'private_limited', icon: '🏭', labelKey: 'regOwnerSub_privateLimited' },
    { value: 'opc', icon: '👤', labelKey: 'regOwnerSub_opc' },
  ],
};
const MAX_KYC_BYTES = 5 * 1024 * 1024;
const KYC_ACCEPT = 'image/jpeg,image/png,image/webp,application/pdf';
const WHATSAPP_INVITE_URL = 'https://wa.me/918408000084';
const REG_PENDING_STORAGE_KEY = 'developmentexpress_reg_pending';

const REGISTER_I18N = {
  en: {
    regHello: 'Hello!',
    regSelectLanguage: 'Select the language you are comfortable with.',
    regContinue: 'Continue',
    regInviteCode: 'Invite Code',
    regInviteCodePlaceholder: 'Enter your invite code',
    regErrInviteCode: 'Invite code is required.',
    regNoInviteCode: "Don't have an invite code? Contact us on WhatsApp",
    regStepRoleTitle: 'Who are you?',
    regStepRoleSub: 'Choose one option to continue registration.',
    regRoleClient: 'Client / Contractor',
    regRoleClientDesc: 'Book excavators, JCB, cranes and more for your projects.',
    regRoleOwner: 'Machine Owner',
    regRoleOwnerDesc: 'Manage fleet, earnings and machine availability.',
    regRoleOperator: 'Operator',
    regRoleOperatorDesc: 'Run assigned machines and daily logs.',
    regRoleComingSoon: 'Coming soon — contact admin',
    regStepClientTypeTitle: 'Select client type',
    regStepClientTypeSub: 'Choose the option closest to your work',
    regTier1Individual: 'Individual / Small',
    regTier1Commercial: 'Commercial Project',
    regTier1Government: 'Government / MSME',
    regSub_individualResidential: 'Individual / Residential',
    regSub_farmer: 'Farmer / Agriculture',
    regSub_wellDigging: 'Well Digging',
    regSub_industrial: 'Industrial / Factory',
    regSub_roadConstruction: 'Road Construction',
    regSub_canalIrrigation: 'Canal / Irrigation',
    regSub_railway: 'Railway Route',
    regSub_reraBuilder: 'RERA Builder',
    regSub_pwdGovernment: 'PWD / Government',
    regSub_msmeCommercial: 'MSME / Commercial',
    regOwnerStepTitle: 'Business entity',
    regOwnerStepSub: 'Select how this owner is registered',
    regOwnerTier1Individual: 'Individual',
    regOwnerTier1Business: 'Business',
    regOwnerTier1Corporate: 'Corporate',
    regOwnerSub_individual: 'Individual / Farmer',
    regOwnerSub_soleProprietor: 'Sole Proprietor',
    regOwnerSub_partnership: 'Partnership Firm',
    regOwnerSub_llp: 'LLP',
    regOwnerSub_privateLimited: 'Private Limited (Pvt Ltd)',
    regOwnerSub_opc: 'One Person Company (OPC)',
    regStepAccountTitle: 'Account details',
    regStepAccountSub: 'Fill in your details — admin approval within 24 hours.',
    regFullName: 'Full Name',
    regMobile: 'Mobile Number (+91)',
    regEmail: 'Email Address',
    regEmailOptional: 'optional',
    regGstin: 'GSTIN',
    regPanUpload: 'PAN Card Upload',
    regPanHint: 'Image or PDF, max 5 MB',
    regPanSelected: 'File selected',
    regKycSection: 'Identity document',
    regPassword: 'Password',
    regConfirmPassword: 'Confirm Password',
    regErrFullName: 'Full name is required (minimum 3 letters, no numbers).',
    regErrMobile: 'A valid 10-digit mobile number is required.',
    regErrEmail: 'Enter a valid email address or leave blank.',
    regErrGstin: 'Valid GSTIN is required for company registration.',
    regErrPan: 'PAN card upload is required (image or PDF, max 5 MB).',
    regErrPassword: 'Password must be at least 8 characters with 1 uppercase, 1 number, and 1 special character.',
    regErrConfirm: 'Passwords do not match.',
    regPasswordWeak: 'Weak',
    regPasswordMedium: 'Medium',
    regPasswordStrong: 'Strong',
    regSubmit: 'Sign Up',
    regSubmitting: 'Submitting...',
    regSuccessTitle: 'Registration submitted',
    regSuccessMessage: 'Admin approval pending (24h). You will be notified once your account is active.',
    ownerNextStep1: 'Application submitted successfully',
    ownerNextStep2: 'Admin review within 24 hours',
    ownerNextStep3: 'WhatsApp notification on approval',
    ownerNextStep4: 'Login → Register machine → Add bank details → Complete KYC',
    regApprovalWaiting: 'Login will appear here automatically once admin approves your account.',
    regApprovalChecking: 'Checking approval status…',
    regBack: 'Back',
    regNext: 'Next',
    regRegisterFailed: 'Registration failed. Please try again.',
    regPanUploadFailed: 'Could not upload PAN document. Please try again.',
    regKycUploadFailed: 'Could not upload identity documents. Please try again.',
    regAppName: 'Development Express',
    regTagline: 'The Gold Standard of Infrastructure',
    regDpdpAccept: 'I have read the Data Collection Notice and consent to processing of my personal data under the DPDP Act, 2023.',
    regDpdpRequired: 'You must accept the Data Collection Notice to register.',
    regErrCin: 'Enter a valid CIN (e.g. U12345MH2020PTC123456).',
    regErrLlpin: 'Enter a valid LLPIN (e.g. AAX-1234).',
    regErrDin: 'Enter a valid 8-digit DIN.',
    regDpdpTitle: 'Data Collection Notice (DPDP Act 2023)',
    regDpdpBody: 'Development Express collects your name, phone, Aadhaar, PAN, and bank details for booking, payment, and KYC purposes. Data is stored securely in India (Supabase). Financial, invoice, GST, TDS, booking and wallet ledger records are retained for as long as required under applicable Indian law (including the Income-tax Act, 1961 and the CGST Act, 2017) — currently up to 8 years — and then deleted. PAN, GSTIN and legal/business name are retained with those records for the same legal purpose. KYC documents (PAN/Aadhaar/GST certificate images), profile photo, bank details and saved addresses are deleted on account deletion, unless required for an ongoing dispute or legal proceeding. You have the right to access, correct, and request erasure of your data. For grievances contact: grievance@developmentexpress.in',
  },
  mr: {
    regHello: 'नमस्कार!',
    regSelectLanguage: 'तुम्हाला सोयीस्कर असलेली भाषा निवडा.',
    regContinue: 'पुढे',
    regInviteCode: 'Invite Code',
    regInviteCodePlaceholder: 'Enter your invite code',
    regErrInviteCode: 'Invite code आवश्यक आहे.',
    regNoInviteCode: 'Invite code नाही? WhatsApp वर संपर्क करा',
    regStepRoleTitle: 'तुम्ही कोण आहात?',
    regStepRoleSub: 'नोंदणीसाठी खालीलपैकी एक निवडा.',
    regRoleClient: 'Client / Contractor',
    regRoleClientDesc: 'Excavator, JCB, crane आणि इतर मशinery बुक करा.',
    regRoleOwner: 'Machine Owner',
    regRoleOwnerDesc: 'Fleet, earnings आणि availability manage करा.',
    regRoleOperator: 'Operator',
    regRoleOperatorDesc: 'Assigned machines आणि daily logs.',
    regRoleComingSoon: 'लवकरच — admin शी संपर्क करा',
    regStepClientTypeTitle: 'क्लायंट प्रकार निवडा',
    regStepClientTypeSub: 'तुमच्या कामाशी जवळचा पर्याय निवडा',
    regTier1Individual: 'वैयक्तिक / लहान',
    regTier1Commercial: 'व्यावसायिक प्रकल्प',
    regTier1Government: 'सरकारी / MSME',
    regSub_individualResidential: 'वैयक्तिक / निवासी',
    regSub_farmer: 'शेतकरी / शेती',
    regSub_wellDigging: 'विहीर खोदणे',
    regSub_industrial: 'औद्योगिक / कारखाना',
    regSub_roadConstruction: 'रस्ता बांधकाम',
    regSub_canalIrrigation: 'कालवा / सिंचन',
    regSub_railway: 'रेल्वे मार्ग',
    regSub_reraBuilder: 'RERA बिल्डर',
    regSub_pwdGovernment: 'PWD / सरकारी',
    regSub_msmeCommercial: 'MSME / व्यावसायिक',
    regOwnerStepTitle: 'व्यवसाय घटक',
    regOwnerStepSub: 'मालकाचा व्यवसाय नोंदणी प्रकार निवडा',
    regOwnerTier1Individual: 'वैयक्तिक',
    regOwnerTier1Business: 'व्यवसाय',
    regOwnerTier1Corporate: 'कॉर्पोरेट',
    regOwnerSub_individual: 'वैयक्तिक / शेतकरी',
    regOwnerSub_soleProprietor: 'एकल मालकी',
    regOwnerSub_partnership: 'भागीदारी फर्म',
    regOwnerSub_llp: 'LLP',
    regOwnerSub_privateLimited: 'खाजगी मर्यादित (Pvt Ltd)',
    regOwnerSub_opc: 'एक व्यक्ती कंपनी (OPC)',
    regStepAccountTitle: 'खाते तपशील',
    regStepAccountSub: 'तपशील भरा — admin approval 24 तासांत.',
    regFullName: 'पूर्ण नाव',
    regMobile: 'मोबाईल नंबर (+91)',
    regEmail: 'Email',
    regEmailOptional: 'optional',
    regGstin: 'GSTIN',
    regPanUpload: 'PAN Card Upload',
    regPanHint: 'Image किंवा PDF, max 5 MB',
    regPanSelected: 'File निवडली',
    regKycSection: 'ओळखपत्र',
    regPassword: 'Password',
    regConfirmPassword: 'Confirm Password',
    regErrFullName: 'पूर्ण नाव आवश्यक आहे (किमान 3 अक्षरे)',
    regErrMobile: '10 अंकी मोबाईल नंबर आवश्यक आहे',
    regErrEmail: 'वैध email टाका किंवा रिकामे ठेवा.',
    regErrGstin: 'Company साठी वैध GSTIN आवश्यक आहे.',
    regErrPan: 'PAN card upload आवश्यक (image/PDF, max 5 MB).',
    regErrPassword: 'पासवर्ड किमान 6 अक्षरे, 1 capital, 1 number, 1 special character असणे आवश्यक',
    regErrConfirm: 'पासवर्ड जुळत नाही',
    regPasswordWeak: 'Weak',
    regPasswordMedium: 'Medium',
    regPasswordStrong: 'Strong',
    regSubmit: 'साइन अप करा',
    regSubmitting: 'सबमिट होत आहे...',
    regSuccessTitle: 'Registration submitted',
    regSuccessMessage: 'Admin approval pending (24h). Account active झाल्यावर कळवले जाईल.',
    ownerNextStep1: 'अर्ज यशस्वीपणे सबमिट झाला',
    ownerNextStep2: '२४ तासांत अॅडमिन पुनरावलोकन',
    ownerNextStep3: 'मंजुरीनंतर WhatsApp सूचना',
    ownerNextStep4: 'लॉगिन → मशीन नोंदवा → बँक तपशील जोडा → KYC पूर्ण करा',
    regApprovalWaiting: 'Admin approve झाल्यावर login button इथेच automatically दिसेल.',
    regApprovalChecking: 'Approval status तपासत आहे…',
    regBack: 'मागे',
    regNext: 'पुढे',
    regRegisterFailed: 'Registration अयशस्वी. पुन्हा प्रयत्न करा.',
    regPanUploadFailed: 'PAN upload अयशस्वी. पुन्हा प्रयत्न करा.',
    regKycUploadFailed: 'Document upload अयशस्वी. पुन्हा प्रयत्न करा.',
    regAppName: 'Development Express',
    regTagline: 'The Gold Standard of Infrastructure',
    regDpdpAccept: 'मी माहिती संकलन सूचना वाचली आहे आणि DPDP कायदा, 2023 अंतर्गत माझ्या वैयक्तिक डेटावर प्रक्रिया करण्यास संमती देतो.',
    regDpdpRequired: 'नोंदणीसाठी माहिती संकलन सूचना स्वीकारणे आवश्यक आहे.',
    regErrCin: 'वैध CIN भरा (उदा. U12345MH2020PTC123456).',
    regErrLlpin: 'वैध LLPIN भरा (उदा. AAX-1234).',
    regErrDin: 'वैध ८ अंकी DIN भरा.',
    regDpdpTitle: 'माहिती संकलन सूचना (DPDP कायदा 2023)',
    regDpdpBody: 'Development Express तुमचे नाव, फोन, आधार, PAN आणि बँक तपशील बुकिंग, पेमेंट आणि KYC साठी संकलित करते. डेटा भारतात (Supabase) सुरक्षितपणे साठवला जातो. आर्थिक, बीजक, GST, TDS, बुकिंग आणि वॉलेट लेजर नोंदी लागू भारतीय कायद्यानुसार (Income-tax Act 1961, CGST Act 2017) आवश्यक असेपर्यंत — सध्या जास्तीत जास्त 8 वर्षे — ठेवल्या जातात आणि नंतर हटवल्या जातात. PAN, GSTIN आणि कायदेशीर/व्यवसाय नाव त्याच कायदेशीर हेतूसाठी त्या नोंदींसोबत ठेवले जाते. KYC कागदपत्रे (PAN/आधार/GST प्रमाणपत्र प्रतिमा), प्रोफाइल फोटो, बँक तपशील आणि जतन केलेले पत्ते खाते हटवल्यावर हटवले जातात, जोपर्यंत चालू वाद किंवा कायदेशीर कार्यवाहीसाठी आवश्यक नसतील. तुम्हाला तुमचा डेटा access, दुरुस्त आणि हटवण्याचा अधिकार आहे. तक्रारींसाठी संपर्क: grievance@developmentexpress.in',
  },
  hi: {
    regHello: 'नमस्ते!',
    regSelectLanguage: 'वह भाषा चुनें जिसमें आप सहज हैं।',
    regContinue: 'आगे बढ़ें',
    regInviteCode: 'Invite Code',
    regInviteCodePlaceholder: 'अपना invite code दर्ज करें',
    regErrInviteCode: 'Invite code आवश्यक है।',
    regNoInviteCode: 'Invite code नहीं है? WhatsApp पर हमसे संपर्क करें',
    regStepRoleTitle: 'आप कौन हैं?',
    regStepRoleSub: 'पंजीकरण जारी रखने के लिए एक विकल्प चुनें।',
    regRoleClient: 'Client / Contractor',
    regRoleClientDesc: 'अपनी परियोजनाओं के लिए excavator, JCB, crane और अन्य मशीनें बुक करें।',
    regRoleOwner: 'Machine Owner',
    regRoleOwnerDesc: 'अपना बेड़ा, कमाई और मशीन की उपलब्धता प्रबंधित करें।',
    regRoleOperator: 'Operator',
    regRoleOperatorDesc: 'निर्दिष्ट मशीनें चलाएँ और दैनिक लॉग भरें।',
    regRoleComingSoon: 'जल्द आ रहा है — एडमिन से संपर्क करें',
    regStepClientTypeTitle: 'क्लाइंट प्रकार चुनें',
    regStepClientTypeSub: 'अपने काम के सबसे करीबी विकल्प चुनें',
    regTier1Individual: 'व्यक्तिगत / छोटा',
    regTier1Commercial: 'व्यावसायिक परियोजना',
    regTier1Government: 'सरकारी / MSME',
    regSub_individualResidential: 'व्यक्तिगत / आवासीय',
    regSub_farmer: 'किसान / कृषि',
    regSub_wellDigging: 'कुआं खुदाई',
    regSub_industrial: 'औद्योगिक / कारखाना',
    regSub_roadConstruction: 'सड़क निर्माण',
    regSub_canalIrrigation: 'नहर / सिंचाई',
    regSub_railway: 'रेलवे रूट',
    regSub_reraBuilder: 'RERA बिल्डर',
    regSub_pwdGovernment: 'PWD / सरकारी',
    regSub_msmeCommercial: 'MSME / व्यावसायिक',
    regOwnerStepTitle: 'व्यवसाय इकाई',
    regOwnerStepSub: 'मालिक का व्यवसाय पंजीकरण प्रकार चुनें',
    regOwnerTier1Individual: 'व्यक्तिगत',
    regOwnerTier1Business: 'व्यवसाय',
    regOwnerTier1Corporate: 'कॉर्पोरेट',
    regOwnerSub_individual: 'व्यक्तिगत / किसान',
    regOwnerSub_soleProprietor: 'एकल स्वामित्व',
    regOwnerSub_partnership: 'साझेदारी फर्म',
    regOwnerSub_llp: 'LLP',
    regOwnerSub_privateLimited: 'प्राइवेट लिमिटेड (Pvt Ltd)',
    regOwnerSub_opc: 'एक व्यक्ति कंपनी (OPC)',
    regStepAccountTitle: 'खाते का विवरण',
    regStepAccountSub: 'अपना विवरण भरें — 24 घंटे में एडमिन की स्वीकृति।',
    regFullName: 'पूरा नाम',
    regMobile: 'मोबाइल नंबर (+91)',
    regEmail: 'Email Address',
    regEmailOptional: 'वैकल्पिक',
    regGstin: 'GSTIN',
    regPanUpload: 'PAN Card Upload',
    regPanHint: 'Image या PDF, अधिकतम 5 MB',
    regPanSelected: 'फ़ाइल चुनी गई',
    regKycSection: 'पहचान दस्तावेज़',
    regPassword: 'पासवर्ड',
    regConfirmPassword: 'पासवर्ड की पुष्टि करें',
    regErrFullName: 'पूरा नाम आवश्यक है (कम से कम 3 अक्षर, संख्या नहीं)।',
    regErrMobile: 'वैध 10-अंकी मोबाइल नंबर आवश्यक है।',
    regErrEmail: 'वैध email दर्ज करें या खाली छोड़ें।',
    regErrGstin: 'Company पंजीकरण के लिए वैध GSTIN आवश्यक है।',
    regErrPan: 'PAN card अपलोड आवश्यक है (image या PDF, अधिकतम 5 MB)।',
    regErrPassword: 'पासवर्ड कम से कम 8 अक्षर का हो, 1 बड़ा अक्षर, 1 संख्या और 1 विशेष अक्षर होना चाहिए।',
    regErrConfirm: 'पासवर्ड मेल नहीं खाते।',
    regPasswordWeak: 'कमज़ोर',
    regPasswordMedium: 'मध्यम',
    regPasswordStrong: 'मज़बूत',
    regSubmit: 'साइन अप करें',
    regSubmitting: 'जमा हो रहा है...',
    regSuccessTitle: 'पंजीकरण जमा हो गया',
    regSuccessMessage: 'एडमिन की स्वीकृति लंबित (24 घं.)। खाता सक्रिय होने पर सूचित किया जाएगा।',
    ownerNextStep1: 'आवेदन सफलतापूर्वक जमा हो गया',
    ownerNextStep2: '२४ घंटे में एडमिन समीक्षा',
    ownerNextStep3: 'स्वीकृति पर WhatsApp सूचना',
    ownerNextStep4: 'लॉगिन → मशीन रजिस्टर करें → बैंक विवरण जोड़ें → KYC पूरा करें',
    regApprovalWaiting: 'एडमिन की स्वीकृति के बाद लॉगिन यहाँ स्वतः दिखेगा।',
    regApprovalChecking: 'स्वीकृति स्थिति जाँची जा रही है…',
    regBack: 'वापस',
    regNext: 'आगे',
    regRegisterFailed: 'पंजीकरण विफल। कृपया पुनः प्रयास करें।',
    regPanUploadFailed: 'PAN दस्तावेज़ अपलोड नहीं हो सका। कृपया पुनः प्रयास करें।',
    regKycUploadFailed: 'पहचान दस्तावेज़ अपलोड नहीं हो सके। कृपया पुनः प्रयास करें।',
    regAppName: 'Development Express',
    regTagline: 'The Gold Standard of Infrastructure',
    regDpdpAccept: 'मैंने डेटा संग्रह सूचना पढ़ ली है और DPDP अधिनियम, 2023 के तहत अपने व्यक्तिगत डेटा के प्रसंस्करण के लिए सहमति देता/देती हूँ।',
    regDpdpRequired: 'पंजीकरण के लिए डेटा संग्रह सूचना स्वीकार करना आवश्यक है।',
    regErrCin: 'मान्य CIN दर्ज करें (उदा. U12345MH2020PTC123456)।',
    regErrLlpin: 'मान्य LLPIN दर्ज करें (उदा. AAX-1234)।',
    regErrDin: 'मान्य 8 अंकों का DIN दर्ज करें।',
    regDpdpTitle: 'डेटा संग्रह सूचना (DPDP अधिनियम 2023)',
    regDpdpBody: 'Development Express आपका नाम, फोन, आधार, PAN और बैंक विवरण बुकिंग, भुगतान और KYC के लिए एकत्र करता है। डेटा भारत में (Supabase) सुरक्षित रूप से संग्रहीत किया जाता है। वित्तीय, चालान, GST, TDS, बुकिंग और वॉलेट लेजर रिकॉर्ड लागू भारतीय कानून (Income-tax Act 1961, CGST Act 2017) के अंतर्गत जितना आवश्यक हो — वर्तमान में अधिकतम 8 वर्ष — रखे जाते हैं और फिर हटा दिए जाते हैं। PAN, GSTIN और कानूनी/व्यावसायिक नाम उसी कानूनी उद्देश्य के लिए उन रिकॉर्ड के साथ रखे जाते हैं। KYC दस्तावेज़ (PAN/आधार/GST प्रमाणपत्र छवियाँ), प्रोफ़ाइल फ़ोटो, बैंक विवरण और सहेजे गए पते खाता हटाने पर हटा दिए जाते हैं, जब तक किसी चल रहे विवाद या कानूनी कार्यवाही के लिए आवश्यक न हों। आपको अपना डेटा access, सुधारने और हटाने का अधिकार है। शिकायतों के लिए संपर्क: grievance@developmentexpress.in',
  },
  gu: {
    regHello: 'નમસ્તે!',
    regSelectLanguage: 'તમને સુવિધાજનક ભાષા પસંદ કરો.',
    regContinue: 'આગળ વધો',
    regInviteCode: 'Invite Code',
    regInviteCodePlaceholder: 'તમારો invite code દાખલ કરો',
    regErrInviteCode: 'Invite code જરૂરી છે.',
    regNoInviteCode: 'Invite code નથી? WhatsApp પર અમારો સંપર્ક કરો',
    regStepRoleTitle: 'તમે કોણ છો?',
    regStepRoleSub: 'નોંધણી ચાલુ રાખવા એક વિકલ્પ પસંદ કરો.',
    regRoleClient: 'Client / Contractor',
    regRoleClientDesc: 'તમારા પ્રોજેક્ટ માટે excavator, JCB, crane અને અન્ય મશીન બુક કરો.',
    regRoleOwner: 'Machine Owner',
    regRoleOwnerDesc: 'તમારો ફ્લીટ, કમાણી અને મશીનની ઉપલબ્ધતા મેનેજ કરો.',
    regRoleOperator: 'Operator',
    regRoleOperatorDesc: 'સોંપેલી મશીન ચલાવો અને દૈનિક લોગ ભરો.',
    regRoleComingSoon: 'ટૂંક સમયમાં — એડમિનનો સંપર્ક કરો',
    regStepClientTypeTitle: 'ક્લાયન્ટ પ્રકાર પસંદ કરો',
    regStepClientTypeSub: 'તમારા કામની નજીકનો વિકલ્પ પસંદ કરો',
    regTier1Individual: 'વ્યક્તિગત / નાનું',
    regTier1Commercial: 'વ્યાવસાયિક પ્રોજેક્ટ',
    regTier1Government: 'સરકારી / MSME',
    regSub_individualResidential: 'વ્યક્તિગત / રહેણાંક',
    regSub_farmer: 'ખેડૂત / કૃષિ',
    regSub_wellDigging: 'કૂવો ખોદવું',
    regSub_industrial: 'ઔદ્યોગિક / કારખાનું',
    regSub_roadConstruction: 'રોડ બાંધકામ',
    regSub_canalIrrigation: 'નહેર / સિંચાઈ',
    regSub_railway: 'રેલ્વે રૂટ',
    regSub_reraBuilder: 'RERA બિલ્ડર',
    regSub_pwdGovernment: 'PWD / સરકારી',
    regSub_msmeCommercial: 'MSME / વ્યાવસાયિક',
    regOwnerStepTitle: 'વ્યવસાય એન્ટિટી',
    regOwnerStepSub: 'માલિકનો વ્યવસાય નોંધણી પ્રકાર પસંદ કરો',
    regOwnerTier1Individual: 'વ્યક્તિગત',
    regOwnerTier1Business: 'વ્યવસાય',
    regOwnerTier1Corporate: 'કોર્પોરેટ',
    regOwnerSub_individual: 'વ્યક્તિગત / ખેડૂત',
    regOwnerSub_soleProprietor: 'એકલ માલિકી',
    regOwnerSub_partnership: 'ભાગીદારી પેઢી',
    regOwnerSub_llp: 'LLP',
    regOwnerSub_privateLimited: 'પ્રાઇવેટ લિમિટેડ (Pvt Ltd)',
    regOwnerSub_opc: 'એક વ્યક્તિ કંપની (OPC)',
    regStepAccountTitle: 'એકાઉન્ટ વિગતો',
    regStepAccountSub: 'તમારી વિગતો ભરો — 24 કલાકમાં એડમિન મંજૂરી.',
    regFullName: 'પૂરું નામ',
    regMobile: 'મોબાઇલ નંબર (+91)',
    regEmail: 'Email Address',
    regEmailOptional: 'વૈકલ્પિક',
    regGstin: 'GSTIN',
    regPanUpload: 'PAN Card Upload',
    regPanHint: 'Image અથવા PDF, વધુમાં વધુ 5 MB',
    regPanSelected: 'ફાઇલ પસંદ કરી',
    regKycSection: 'ઓળખ દસ્તાવેજ',
    regPassword: 'પાસવર્ડ',
    regConfirmPassword: 'પાસવર્ડની પુષ્ટિ કરો',
    regErrFullName: 'પૂરું નામ જરૂરી છે (ઓછામાં ઓછા 3 અક્ષર, સંખ્યા નહીં).',
    regErrMobile: 'માન્ય 10-અંકનો મોબાઇલ નંબર જરૂરી છે.',
    regErrEmail: 'માન્ય email દાખલ કરો અથવા ખાલી છોડો.',
    regErrGstin: 'Company નોંધણી માટે માન્ય GSTIN જરૂરી છે.',
    regErrPan: 'PAN card અપલોડ જરૂરી છે (image અથવા PDF, વધુમાં વધુ 5 MB).',
    regErrPassword: 'પાસવર્ડ ઓછામાં ઓછા 8 અક્ષરનો હોવો જોઈએ, 1 મોટો અક્ષર, 1 સંખ્યા અને 1 વિશેષ અક્ષર.',
    regErrConfirm: 'પાસવર્ડ મેળ ખાતા નથી.',
    regPasswordWeak: 'નબળો',
    regPasswordMedium: 'મધ્યમ',
    regPasswordStrong: 'મજબૂત',
    regSubmit: 'સાઇન અપ કરો',
    regSubmitting: 'સબમિટ થઈ રહ્યું છે...',
    regSuccessTitle: 'નોંધણી સબમિટ થઈ',
    regSuccessMessage: 'એડમિન મંજૂરી બાકી (24 ક.). એકાઉન્ટ સક્રિય થયા પછી સૂચના મળશે.',
    ownerNextStep1: 'અરજી સફળતાપૂર્વક સબમિટ થઈ',
    ownerNextStep2: '૨૪ કલાકમાં એડમિન સમીક્ષા',
    ownerNextStep3: 'મંજૂરી પર WhatsApp સૂચના',
    ownerNextStep4: 'લૉગિન → મશીન નોંધણી → બેંક વિગતો ઉમેરો → KYC પૂર્ણ કરો',
    regApprovalWaiting: 'એડમિન મંજૂર થયા પછી લોગિન અહીં આપમેળે દેખાશે.',
    regApprovalChecking: 'મંજૂરી સ્થિતિ તપાસી રહ્યા છીએ…',
    regBack: 'પાછા',
    regNext: 'આગળ',
    regRegisterFailed: 'નોંધણી નિષ્ફળ. કૃપા કરીને ફરી પ્રયાસ કરો.',
    regPanUploadFailed: 'PAN દસ્તાવેજ અપલોડ થયો નહીં. કૃપા કરીને ફરી પ્રયાસ કરો.',
    regKycUploadFailed: 'ઓળખ દસ્તાવેજ અપલોડ થયા નહીં. કૃપા કરીને ફરી પ્રયાસ કરો.',
    regAppName: 'Development Express',
    regTagline: 'The Gold Standard of Infrastructure',
  },
  ta: {
    regHello: 'வணக்கம்!',
    regSelectLanguage: 'உங்களுக்கு வசதியான மொழியைத் தேர்ந்தெடுக்கவும்.',
    regContinue: 'தொடரவும்',
    regInviteCode: 'Invite Code',
    regInviteCodePlaceholder: 'உங்கள் invite code ஐ உள்ளிடவும்',
    regErrInviteCode: 'Invite code தேவை.',
    regNoInviteCode: 'Invite code இல்லையா? WhatsApp இல் எங்களைத் தொடர்பு கொள்ளுங்கள்',
    regStepRoleTitle: 'நீங்கள் யார்?',
    regStepRoleSub: 'பதிவைத் தொடர ஒரு விருப்பத்தைத் தேர்ந்தெடுக்கவும்.',
    regRoleClient: 'Client / Contractor',
    regRoleClientDesc: 'உங்கள் திட்டங்களுக்கு excavator, JCB, crane மற்றும் பிற இயந்திரங்களை முன்பதிவு செய்யுங்கள்.',
    regRoleOwner: 'Machine Owner',
    regRoleOwnerDesc: 'உங்கள் வாகனங்கள், வருவாய் மற்றும் இயந்திர கிடைப்பை நிர்வகிக்கவும்.',
    regRoleOperator: 'Operator',
    regRoleOperatorDesc: 'ஒதுக்கப்பட்ட இயந்திரங்களை இயக்கி தினசரி பதிவுகளை வைக்கவும்.',
    regRoleComingSoon: 'விரைவில் — நிர்வாகியைத் தொடர்பு கொள்ளுங்கள்',
    regStepClientTypeTitle: 'வாடிக்கையாளர் வகையைத் தேர்ந்தெடுக்கவும்',
    regStepClientTypeSub: 'உங்கள் வேலைக்கு நெருக்கமான விருப்பத்தைத் தேர்ந்தெடுக்கவும்',
    regTier1Individual: 'தனிநபர் / சிறியது',
    regTier1Commercial: 'வணிகத் திட்டம்',
    regTier1Government: 'அரசு / MSME',
    regSub_individualResidential: 'தனிநபர் / குடியிருப்பு',
    regSub_farmer: 'விவசாயி / வேளாண்மை',
    regSub_wellDigging: 'கிணறு தோண்டுதல்',
    regSub_industrial: 'தொழில்துறை / தொழிற்சாலை',
    regSub_roadConstruction: 'சாலை கட்டுமானம்',
    regSub_canalIrrigation: 'கால்வாய் / நீர்ப்பாசனம்',
    regSub_railway: 'இரயில்வே பாதை',
    regSub_reraBuilder: 'RERA கட்டிட நிறுவனம்',
    regSub_pwdGovernment: 'PWD / அரசு',
    regSub_msmeCommercial: 'MSME / வணிக',
    regOwnerStepTitle: 'வணிக நிறுவன வகை',
    regOwnerStepSub: 'உரிமையாளரின் வணிக பதிவு வகையைத் தேர்ந்தெடுக்கவும்',
    regOwnerTier1Individual: 'தனிநபர்',
    regOwnerTier1Business: 'வணிகம்',
    regOwnerTier1Corporate: 'கார்பொரேட்',
    regOwnerSub_individual: 'தனிநபர் / விவசாயி',
    regOwnerSub_soleProprietor: 'தனி உரிமையாளர்',
    regOwnerSub_partnership: 'கூட்டாண்மை நிறுவனம்',
    regOwnerSub_llp: 'LLP',
    regOwnerSub_privateLimited: 'பிரைவேட் லிமிடெட் (Pvt Ltd)',
    regOwnerSub_opc: 'ஒரு நபர் நிறுவனம் (OPC)',
    regStepAccountTitle: 'கணக்கு விவரங்கள்',
    regStepAccountSub: 'உங்கள் விவரங்களை நிரப்புங்கள் — 24 மணி நேரத்தில் நிர்வாகி ஒப்புதல்.',
    regFullName: 'முழு பெயர்',
    regMobile: 'மொபைல் எண் (+91)',
    regEmail: 'Email Address',
    regEmailOptional: 'விருப்பம்',
    regGstin: 'GSTIN',
    regPanUpload: 'PAN Card Upload',
    regPanHint: 'Image அல்லது PDF, அதிகபட்சம் 5 MB',
    regPanSelected: 'கோப்பு தேர்ந்தெடுக்கப்பட்டது',
    regKycSection: 'அடையாள ஆவணம்',
    regPassword: 'கடவுச்சொல்',
    regConfirmPassword: 'கடவுச்சொல்லை உறுதிப்படுத்தவும்',
    regErrFullName: 'முழு பெயர் தேவை (குறைந்தது 3 எழுத்துகள், எண்கள் இல்லை).',
    regErrMobile: 'செல்லுபடியாகும் 10 இலக்க மொபைல் எண் தேவை.',
    regErrEmail: 'செல்லுபடியாகும் email உள்ளிடவும் அல்லது காலியாக விடவும்.',
    regErrGstin: 'Company பதிவுக்கு செல்லுபடியாகும் GSTIN தேவை.',
    regErrPan: 'PAN card பதிவேற்றம் தேவை (image அல்லது PDF, அதிகபட்சம் 5 MB).',
    regErrPassword: 'கடவுச்சொல் குறைந்தது 8 எழுத்துகள், 1 பெரிய எழுத்து, 1 எண் மற்றும் 1 சிறப்பு எழுத்து இருக்க வேண்டும்.',
    regErrConfirm: 'கடவுச்சொற்கள் பொருந்தவில்லை.',
    regPasswordWeak: 'பலவீனம்',
    regPasswordMedium: 'நடுத்தரம்',
    regPasswordStrong: 'வலிமையானது',
    regSubmit: 'பதிவு செய்யுங்கள்',
    regSubmitting: 'சமர்ப்பிக்கப்படுகிறது...',
    regSuccessTitle: 'பதிவு சமர்ப்பிக்கப்பட்டது',
    regSuccessMessage: 'நிர்வாகி ஒப்புதல் நிலுவையில் (24 மணி). கணக்கு செயல்படுத்தப்பட்டதும் அறிவிக்கப்படும்.',
    ownerNextStep1: 'விண்ணப்பம் வெற்றிகரமாக சமர்ப்பிக்கப்பட்டது',
    ownerNextStep2: '24 மணி நேரத்தில் நிர்வாகி மதிப்பாய்வு',
    ownerNextStep3: 'ஒப்புதலில் WhatsApp அறிவிப்பு',
    ownerNextStep4: 'உள்நுழைவு → இயந்திரம் பதிவு → வங்கி விவரம் → KYC முடிக்கவும்',
    regApprovalWaiting: 'நிர்வாகி ஒப்புதல் கிடைத்ததும் உள்நுழைவு இங்கே தானாக தோன்றும்.',
    regApprovalChecking: 'ஒப்புதல் நிலை சரிபார்க்கப்படுகிறது…',
    regBack: 'பின்செல்',
    regNext: 'அடுத்து',
    regRegisterFailed: 'பதிவு தோல்வியடைந்தது. மீண்டும் முயற்சிக்கவும்.',
    regPanUploadFailed: 'PAN ஆவணம் பதிவேற்றம் தோல்வி. மீண்டும் முயற்சிக்கவும்.',
    regKycUploadFailed: 'அடையாள ஆவணங்கள் பதிவேற்றம் தோல்வி. மீண்டும் முயற்சிக்கவும்.',
    regAppName: 'Development Express',
    regTagline: 'The Gold Standard of Infrastructure',
  },
  te: {
    regHello: 'నమస్కారం!',
    regSelectLanguage: 'మీకు సౌకర్యవంతమైన భాషను ఎంచుకోండి.',
    regContinue: 'కొనసాగించండి',
    regInviteCode: 'Invite Code',
    regInviteCodePlaceholder: 'మీ invite code నమోదు చేయండి',
    regErrInviteCode: 'Invite code అవసరం.',
    regNoInviteCode: 'Invite code లేదా? WhatsApp లో మమ్మల్ని సంప్రదించండి',
    regStepRoleTitle: 'మీరు ఎవరు?',
    regStepRoleSub: 'నమోదు కొనసాగించడానికి ఒక ఎంపికను ఎంచుకోండి.',
    regRoleClient: 'Client / Contractor',
    regRoleClientDesc: 'మీ ప్రాజెక్ట్‌ల కోసం excavator, JCB, crane మరియు ఇతర మెషీన్లు బుక్ చేయండి.',
    regRoleOwner: 'Machine Owner',
    regRoleOwnerDesc: 'మీ ఫ్లీట్, ఆదాయం మరియు మెషీన్ లభ్యతను నిర్వహించండి.',
    regRoleOperator: 'Operator',
    regRoleOperatorDesc: 'కేటాయించిన మెషీన్లు నడపండి మరియు రోజువారీ లాగ్‌లు నమోదు చేయండి.',
    regRoleComingSoon: 'త్వరలో — అడ్మిన్‌ను సంప్రదించండి',
    regStepClientTypeTitle: 'క్లయింట్ రకాన్ని ఎంచుకోండి',
    regStepClientTypeSub: 'మీ పని దగ్గరగా ఉన్న ఎంపికను ఎంచుకోండి',
    regTier1Individual: 'వ్యక్తిగత / చిన్న',
    regTier1Commercial: 'వాణిజ్య ప్రాజెక్ట్',
    regTier1Government: 'ప్రభుత్వ / MSME',
    regSub_individualResidential: 'వ్యక్తిగత / నివాస',
    regSub_farmer: 'రైతు / వ్యవసాయం',
    regSub_wellDigging: 'బావి తవ్వకం',
    regSub_industrial: 'పారిశ్రామిక / కర్మాగారం',
    regSub_roadConstruction: 'రోడ్డు నిర్మాణం',
    regSub_canalIrrigation: 'కాలువ / నీటిపారుదల',
    regSub_railway: 'రైల్వే మార్గం',
    regSub_reraBuilder: 'RERA బిల్డర్',
    regSub_pwdGovernment: 'PWD / ప్రభుత్వ',
    regSub_msmeCommercial: 'MSME / వాణిజ్య',
    regOwnerStepTitle: 'వ్యాపార సంస్థ',
    regOwnerStepSub: 'యజమాని వ్యాపార నమోదు రకాన్ని ఎంచుకోండి',
    regOwnerTier1Individual: 'వ్యక్తిగత',
    regOwnerTier1Business: 'వ్యాపారం',
    regOwnerTier1Corporate: 'కార్పొరేట్',
    regOwnerSub_individual: 'వ్యక్తిగత / రైతు',
    regOwnerSub_soleProprietor: 'సోల్ ప్రొప్రైటర్',
    regOwnerSub_partnership: 'పార్టనర్‌షిప్ సంస్థ',
    regOwnerSub_llp: 'LLP',
    regOwnerSub_privateLimited: 'ప్రైవేట్ లిమిటెడ్ (Pvt Ltd)',
    regOwnerSub_opc: 'వన్ పర్సన్ కంపెనీ (OPC)',
    regStepAccountTitle: 'ఖాతా వివరాలు',
    regStepAccountSub: 'మీ వివరాలు నమోదు చేయండి — 24 గంటల్లో అడ్మిన్ ఆమోదం.',
    regFullName: 'పూర్తి పేరు',
    regMobile: 'మొబైల్ నంబర్ (+91)',
    regEmail: 'Email Address',
    regEmailOptional: 'ఐచ్ఛికం',
    regGstin: 'GSTIN',
    regPanUpload: 'PAN Card Upload',
    regPanHint: 'Image లేదా PDF, గరిష్ఠంగా 5 MB',
    regPanSelected: 'ఫైల్ ఎంచుకోబడింది',
    regKycSection: 'గుర్తింపు పత్రం',
    regPassword: 'పాస్‌వర్డ్',
    regConfirmPassword: 'పాస్‌వర్డ్ నిర్ధారించండి',
    regErrFullName: 'పూర్తి పేరు అవసరం (కనీసం 3 అక్షరాలు, సంఖ్యలు లేకుండా).',
    regErrMobile: 'చెల్లుబాటు అయ్యే 10 అంకెల మొబైల్ నంబర్ అవసరం.',
    regErrEmail: 'చెల్లుబాటు అయ్యే email నమోదు చేయండి లేదా ఖాళీగా వదిలేయండి.',
    regErrGstin: 'Company నమోదు కోసం చెల్లుబాటు అయ్యే GSTIN అవసరం.',
    regErrPan: 'PAN card అప్‌లోడ్ అవసరం (image లేదా PDF, గరిష్ఠంగా 5 MB).',
    regErrPassword: 'పాస్‌వర్డ్ కనీసం 8 అక్షరాలు, 1 పెద్ద అక్షరం, 1 సంఖ్య మరియు 1 ప్రత్యేక అక్షరం ఉండాలి.',
    regErrConfirm: 'పాస్‌వర్డ్‌లు సరిపోలడం లేదు.',
    regPasswordWeak: 'బలహీనం',
    regPasswordMedium: 'మధ్యస్థం',
    regPasswordStrong: 'బలమైనది',
    regSubmit: 'సైన్ అప్ చేయండి',
    regSubmitting: 'సమర్పిస్తోంది...',
    regSuccessTitle: 'నమోదు సమర్పించబడింది',
    regSuccessMessage: 'అడ్మిన్ ఆమోదం పెండింగ్‌లో (24 గం.). ఖాతా సక్రియమైన తర్వాత తెలియజేస్తాము.',
    ownerNextStep1: 'దరఖాస్తు విజయవంతంగా సమర్పించబడింది',
    ownerNextStep2: '24 గంటల్లో అడ్మిన్ సమీక్ష',
    ownerNextStep3: 'ఆమోదం మీద WhatsApp నోటిఫికేషన్',
    ownerNextStep4: 'లాగిన్ → మెషీన్ నమోదు → బ్యాంక్ వివరాలు → KYC పూర్తి చేయండి',
    regApprovalWaiting: 'అడ్మిన్ ఆమోదం తర్వాత లాగిన్ ఇక్కడ స్వయంచాలకంగా కనిపిస్తుంది.',
    regApprovalChecking: 'ఆమోద స్థితి తనిఖీ చేస్తోంది…',
    regBack: 'వెనుకకు',
    regNext: 'తదుపరి',
    regRegisterFailed: 'నమోదు విఫలమైంది. దయచేసి మళ్లీ ప్రయత్నించండి.',
    regPanUploadFailed: 'PAN పత్రం అప్‌లోడ్ కాలేదు. దయచేసి మళ్లీ ప్రయత్నించండి.',
    regKycUploadFailed: 'గుర్తింపు పత్రాలు అప్‌లోడ్ కాలేదు. దయచేసి మళ్లీ ప్రయత్నించండి.',
    regAppName: 'Development Express',
    regTagline: 'The Gold Standard of Infrastructure',
  },
  kn: {
    regHello: 'ನಮಸ್ಕಾರ!',
    regSelectLanguage: 'ನಿಮಗೆ ಅನುಕೂಲವಾದ ಭಾಷೆಯನ್ನು ಆಯ್ಕೆಮಾಡಿ.',
    regContinue: 'ಮುಂದುವರಿಸಿ',
    regInviteCode: 'Invite Code',
    regInviteCodePlaceholder: 'ನಿಮ್ಮ invite code ನಮೂದಿಸಿ',
    regErrInviteCode: 'Invite code ಅಗತ್ಯವಿದೆ.',
    regNoInviteCode: 'Invite code ಇಲ್ಲವೇ? WhatsApp ನಲ್ಲಿ ನಮ್ಮನ್ನು ಸಂಪರ್ಕಿಸಿ',
    regStepRoleTitle: 'ನೀವು ಯಾರು?',
    regStepRoleSub: 'ನೋಂದಣಿ ಮುಂದುವರಿಸಲು ಒಂದು ಆಯ್ಕೆಯನ್ನು ಆರಿಸಿ.',
    regRoleClient: 'Client / Contractor',
    regRoleClientDesc: 'ನಿಮ್ಮ ಯೋಜನೆಗಳಿಗೆ excavator, JCB, crane ಮತ್ತು ಇತರ ಯಂತ್ರಗಳನ್ನು ಬುಕ್ ಮಾಡಿ.',
    regRoleOwner: 'Machine Owner',
    regRoleOwnerDesc: 'ನಿಮ್ಮ ಫ್ಲೀಟ್, ಆದಾಯ ಮತ್ತು ಯಂತ್ರ ಲಭ್ಯತೆಯನ್ನು ನಿರ್ವಹಿಸಿ.',
    regRoleOperator: 'Operator',
    regRoleOperatorDesc: 'ನಿಯೋಜಿತ ಯಂತ್ರಗಳನ್ನು ಚಲಾಯಿಸಿ ಮತ್ತು ದೈನಂದಿನ ಲಾಗ್‌ಗಳನ್ನು ನಮೂದಿಸಿ.',
    regRoleComingSoon: 'ಶೀಘ್ರದಲ್ಲೇ — ಆಡ್ಮಿನ್ ಅನ್ನು ಸಂಪರ್ಕಿಸಿ',
    regStepClientTypeTitle: 'ಕ್ಲೈಂಟ್ ಪ್ರಕಾರ ಆಯ್ಕೆಮಾಡಿ',
    regStepClientTypeSub: 'ನಿಮ್ಮ ಕೆಲಸಕ್ಕೆ ಹತ್ತಿರದ ಆಯ್ಕೆ ಆಯ್ಕೆಮಾಡಿ',
    regTier1Individual: 'ವ್ಯಕ್ತಿಗತ / ಸಣ್ಣ',
    regTier1Commercial: 'ವಾಣಿಜ್ಯ ಯೋಜನೆ',
    regTier1Government: 'ಸರ್ಕಾರ / MSME',
    regSub_individualResidential: 'ವ್ಯಕ್ತಿಗತ / ವಸತಿ',
    regSub_farmer: 'ರೈತ / ಕೃಷಿ',
    regSub_wellDigging: 'ಬಾವಿ ಅಗೆಯುವಿಕೆ',
    regSub_industrial: 'ಕೈಗಾರಿಕಾ / ಕಾರ್ಖಾನೆ',
    regSub_roadConstruction: 'ರಸ್ತೆ ನಿರ್ಮಾಣ',
    regSub_canalIrrigation: 'ಕಾಲುವೆ / ನೀರಾವರಿ',
    regSub_railway: 'ರೈಲ್ವೆ ಮಾರ್ಗ',
    regSub_reraBuilder: 'RERA ಬಿಲ್ಡರ್',
    regSub_pwdGovernment: 'PWD / ಸರ್ಕಾರ',
    regSub_msmeCommercial: 'MSME / ವಾಣಿಜ್ಯ',
    regOwnerStepTitle: 'ವ್ಯವಹಾರ ಘಟಕ',
    regOwnerStepSub: 'ಮಾಲೀಕರ ವ್ಯಾಪಾರ ನೋಂದಣಿ ಪ್ರಕಾರ ಆಯ್ಕೆಮಾಡಿ',
    regOwnerTier1Individual: 'ವ್ಯಕ್ತಿಗತ',
    regOwnerTier1Business: 'ವ್ಯಾಪಾರ',
    regOwnerTier1Corporate: 'ಕಾರ್ಪೊರೇಟ್',
    regOwnerSub_individual: 'ವ್ಯಕ್ತಿಗತ / ರೈತ',
    regOwnerSub_soleProprietor: 'ಏಕಾಂಗಿ ಮಾಲೀಕ',
    regOwnerSub_partnership: 'ಪಾಲುದಾರಿ ಸಂಸ್ಥೆ',
    regOwnerSub_llp: 'LLP',
    regOwnerSub_privateLimited: 'ಪ್ರೈವೇಟ್ ಲಿಮಿಟೆಡ್ (Pvt Ltd)',
    regOwnerSub_opc: 'ಒಬ್ಬ ವ್ಯಕ್ತಿ ಕಂಪನಿ (OPC)',
    regStepAccountTitle: 'ಖಾತೆ ವಿವರಗಳು',
    regStepAccountSub: 'ನಿಮ್ಮ ವಿವರಗಳನ್ನು ಭರ್ತಿ ಮಾಡಿ — 24 ಗಂಟೆಗಳಲ್ಲಿ ಆಡ್ಮಿನ್ ಅನುಮೋದನೆ.',
    regFullName: 'ಪೂರ್ಣ ಹೆಸರು',
    regMobile: 'ಮೊಬೈಲ್ ಸಂಖ್ಯೆ (+91)',
    regEmail: 'Email Address',
    regEmailOptional: 'ಐಚ್ಛಿಕ',
    regGstin: 'GSTIN',
    regPanUpload: 'PAN Card Upload',
    regPanHint: 'Image ಅಥವಾ PDF, ಗರಿಷ್ಠ 5 MB',
    regPanSelected: 'ಫೈಲ್ ಆಯ್ಕೆಮಾಡಲಾಗಿದೆ',
    regKycSection: 'ಗುರುತಿನ ದಾಖಲೆ',
    regPassword: 'ಪಾಸ್‌ವರ್ಡ್',
    regConfirmPassword: 'ಪಾಸ್‌ವರ್ಡ್ ದೃಢೀಕರಿಸಿ',
    regErrFullName: 'ಪೂರ್ಣ ಹೆಸರು ಅಗತ್ಯ (ಕನಿಷ್ಠ 3 ಅಕ್ಷರಗಳು, ಸಂಖ್ಯೆಗಳಿಲ್ಲ).',
    regErrMobile: 'ಮಾನ್ಯ 10 ಅಂಕಿಯ ಮೊಬೈಲ್ ಸಂಖ್ಯೆ ಅಗತ್ಯ.',
    regErrEmail: 'ಮಾನ್ಯ email ನಮೂದಿಸಿ ಅಥವಾ ಖಾಲಿ ಬಿಡಿ.',
    regErrGstin: 'Company ನೋಂದಣಿಗೆ ಮಾನ್ಯ GSTIN ಅಗತ್ಯ.',
    regErrPan: 'PAN card ಅಪ್‌ಲೋಡ್ ಅಗತ್ಯ (image ಅಥವಾ PDF, ಗರಿಷ್ಠ 5 MB).',
    regErrPassword: 'ಪಾಸ್‌ವರ್ಡ್ ಕನಿಷ್ಠ 8 ಅಕ್ಷರಗಳು, 1 ದೊಡ್ಡ ಅಕ್ಷರ, 1 ಸಂಖ್ಯೆ ಮತ್ತು 1 ವಿಶೇಷ ಅಕ್ಷರ ಇರಬೇಕು.',
    regErrConfirm: 'ಪಾಸ್‌ವರ್ಡ್‌ಗಳು ಹೊಂದಿಕೆಯಾಗುತ್ತಿಲ್ಲ.',
    regPasswordWeak: 'ದುರ್ಬಲ',
    regPasswordMedium: 'ಮಧ್ಯಮ',
    regPasswordStrong: 'ಬಲಶಾಲಿ',
    regSubmit: 'ಸೈನ್ ಅಪ್ ಮಾಡಿ',
    regSubmitting: 'ಸಲ್ಲಿಸಲಾಗುತ್ತಿದೆ...',
    regSuccessTitle: 'ನೋಂದಣಿ ಸಲ್ಲಿಸಲಾಗಿದೆ',
    regSuccessMessage: 'ಆಡ್ಮಿನ್ ಅನುಮೋದನೆ ಬಾಕಿ (24 ಗಂ.). ಖಾತೆ ಸಕ್ರಿಯವಾದ ನಂತರ ತಿಳಿಸಲಾಗುತ್ತದೆ.',
    ownerNextStep1: 'ಅರ್ಜಿ ಯಶಸ್ವಿಯಾಗಿ ಸಲ್ಲಿಸಲಾಗಿದೆ',
    ownerNextStep2: '24 ಗಂಟೆಗಳಲ್ಲಿ ಆಡ್ಮಿನ್ ಪರಿಶೀಲನೆ',
    ownerNextStep3: 'ಅನುಮೋದನೆಯ ಮೇಲೆ WhatsApp ಅಧಿಸೂಚನೆ',
    ownerNextStep4: 'ಲಾಗಿನ್ → ಯಂತ್ರ ನೋಂದಣಿ → ಬ್ಯಾಂಕ್ ವಿವರ → KYC ಪೂರ್ಣಗೊಳಿಸಿ',
    regApprovalWaiting: 'ಆಡ್ಮಿನ್ ಅನುಮೋದನೆ ನಂತರ ಲಾಗಿನ್ ಇಲ್ಲಿ ಸ್ವಯಂಚಾಲಿತವಾಗಿ ಕಾಣಿಸುತ್ತದೆ.',
    regApprovalChecking: 'ಅನುಮೋದನೆ ಸ್ಥಿತಿ ಪರಿಶೀಲಿಸಲಾಗುತ್ತಿದೆ…',
    regBack: 'ಹಿಂದೆ',
    regNext: 'ಮುಂದೆ',
    regRegisterFailed: 'ನೋಂದಣಿ ವಿಫಲವಾಯಿತು. ದಯವಿಟ್ಟು ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ.',
    regPanUploadFailed: 'PAN ದಾಖಲೆ ಅಪ್‌ಲೋಡ್ ಆಗಲಿಲ್ಲ. ದಯವಿಟ್ಟು ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ.',
    regKycUploadFailed: 'ಗುರುತಿನ ದಾಖಲೆಗಳು ಅಪ್‌ಲೋಡ್ ಆಗಲಿಲ್ಲ. ದಯವಿಟ್ಟು ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ.',
    regAppName: 'Development Express',
    regTagline: 'The Gold Standard of Infrastructure',
  },
  bn: {
    regHello: 'নমস্কার!',
    regSelectLanguage: 'আপনার সুবিধাজনক ভাষা বেছে নিন।',
    regContinue: 'এগিয়ে যান',
    regInviteCode: 'Invite Code',
    regInviteCodePlaceholder: 'আপনার invite code লিখুন',
    regErrInviteCode: 'Invite code প্রয়োজন।',
    regNoInviteCode: 'Invite code নেই? WhatsApp-এ আমাদের সাথে যোগাযোগ করুন',
    regStepRoleTitle: 'আপনি কে?',
    regStepRoleSub: 'নিবন্ধন চালিয়ে যেতে একটি বিকল্প বেছে নিন।',
    regRoleClient: 'Client / Contractor',
    regRoleClientDesc: 'আপনার প্রকল্পের জন্য excavator, JCB, crane এবং অন্যান্য মেশিন বুক করুন।',
    regRoleOwner: 'Machine Owner',
    regRoleOwnerDesc: 'আপনার ফ্লিট, আয় এবং মেশিনের প্রাপ্যতা পরিচালনা করুন।',
    regRoleOperator: 'Operator',
    regRoleOperatorDesc: 'নির্ধারিত মেশিন চালান এবং দৈনিক লগ পূরণ করুন।',
    regRoleComingSoon: 'শীঘ্রই আসছে — অ্যাডমিনের সাথে যোগাযোগ করুন',
    regStepClientTypeTitle: 'ক্লায়েন্ট ধরন বেছে নিন',
    regStepClientTypeSub: 'আপনার কাজের কাছাকাছি বিকল্পটি বেছে নিন',
    regTier1Individual: 'ব্যক্তিগত / ছোট',
    regTier1Commercial: 'বাণিজ্যিক প্রকল্প',
    regTier1Government: 'সরকারি / MSME',
    regSub_individualResidential: 'ব্যক্তিগত / আবাসিক',
    regSub_farmer: 'কৃষক / কৃষি',
    regSub_wellDigging: 'কূপ খনন',
    regSub_industrial: 'শিল্প / কারখানা',
    regSub_roadConstruction: 'সড়ক নির্মাণ',
    regSub_canalIrrigation: 'খাল / সেচ',
    regSub_railway: 'রেলওয়ে রুট',
    regSub_reraBuilder: 'RERA বিল্ডার',
    regSub_pwdGovernment: 'PWD / সরকারি',
    regSub_msmeCommercial: 'MSME / বাণিজ্যিক',
    regOwnerStepTitle: 'ব্যবসায়িক সত্তা',
    regOwnerStepSub: 'মালিকের ব্যবসায়িক নিবন্ধন ধরন বেছে নিন',
    regOwnerTier1Individual: 'ব্যক্তিগত',
    regOwnerTier1Business: 'ব্যবসা',
    regOwnerTier1Corporate: 'কর্পোরেট',
    regOwnerSub_individual: 'ব্যক্তিগত / কৃষক',
    regOwnerSub_soleProprietor: 'একক মালিকানা',
    regOwnerSub_partnership: 'অংশীদারি প্রতিষ্ঠান',
    regOwnerSub_llp: 'LLP',
    regOwnerSub_privateLimited: 'প্রাইভেট লিমিটেড (Pvt Ltd)',
    regOwnerSub_opc: 'এক ব্যক্তি কোম্পানি (OPC)',
    regStepAccountTitle: 'অ্যাকাউন্টের বিবরণ',
    regStepAccountSub: 'আপনার বিবরণ পূরণ করুন — ২৪ ঘণ্টার মধ্যে অ্যাডমিন অনুমোদন।',
    regFullName: 'পুরো নাম',
    regMobile: 'মোবাইল নম্বর (+91)',
    regEmail: 'Email Address',
    regEmailOptional: 'ঐচ্ছিক',
    regGstin: 'GSTIN',
    regPanUpload: 'PAN Card Upload',
    regPanHint: 'Image বা PDF, সর্বোচ্চ 5 MB',
    regPanSelected: 'ফাইল নির্বাচিত',
    regKycSection: 'পরিচয়পত্র',
    regPassword: 'পাসওয়ার্ড',
    regConfirmPassword: 'পাসওয়ার্ড নিশ্চিত করুন',
    regErrFullName: 'পুরো নাম প্রয়োজন (কমপক্ষে ৩টি অক্ষর, সংখ্যা নয়)।',
    regErrMobile: 'বৈধ ১০-অঙ্কের মোবাইল নম্বর প্রয়োজন।',
    regErrEmail: 'বৈধ email লিখুন অথবা খালি রাখুন।',
    regErrGstin: 'Company নিবন্ধনের জন্য বৈধ GSTIN প্রয়োজন।',
    regErrPan: 'PAN card আপলোড প্রয়োজন (image বা PDF, সর্বোচ্চ 5 MB)।',
    regErrPassword: 'পাসওয়ার্ড কমপক্ষে ৮ অক্ষর, ১টি বড় হাতের অক্ষর, ১টি সংখ্যা ও ১টি বিশেষ অক্ষর থাকতে হবে।',
    regErrConfirm: 'পাসওয়ার্ড মিলছে না।',
    regPasswordWeak: 'দুর্বল',
    regPasswordMedium: 'মাঝারি',
    regPasswordStrong: 'শক্তিশালী',
    regSubmit: 'সাইন আপ করুন',
    regSubmitting: 'জমা দেওয়া হচ্ছে...',
    regSuccessTitle: 'নিবন্ধন জমা হয়েছে',
    regSuccessMessage: 'অ্যাডমিন অনুমোদন বাকি (২৪ ঘণ্টা)। অ্যাকাউন্ট সক্রিয় হলে জানানো হবে।',
    ownerNextStep1: 'আবেদন সফলভাবে জমা হয়েছে',
    ownerNextStep2: '২৪ ঘন্টার মধ্যে অ্যাডমিন পর্যালোচনা',
    ownerNextStep3: 'অনুমোদনে WhatsApp বিজ্ঞপ্তি',
    ownerNextStep4: 'লগইন → মেশিন নিবন্ধন → ব্যাংক বিবরণ → KYC সম্পূর্ণ করুন',
    regApprovalWaiting: 'অ্যাডমিন অনুমোদনের পর লগইন এখানে স্বয়ংক্রিয়ভাবে দেখা যাবে।',
    regApprovalChecking: 'অনুমোদনের অবস্থা যাচাই করা হচ্ছে…',
    regBack: 'পিছনে',
    regNext: 'পরবর্তী',
    regRegisterFailed: 'নিবন্ধন ব্যর্থ। অনুগ্রহ করে আবার চেষ্টা করুন।',
    regPanUploadFailed: 'PAN নথি আপলোড হয়নি। অনুগ্রহ করে আবার চেষ্টা করুন।',
    regKycUploadFailed: 'পরিচয়পত্র আপলোড হয়নি। অনুগ্রহ করে আবার চেষ্টা করুন।',
    regAppName: 'Development Express',
    regTagline: 'The Gold Standard of Infrastructure',
  },
  ml: {
    regHello: 'നമസ്കാരം!',
    regSelectLanguage: 'നിങ്ങൾക്ക് സുഖകരമായ ഭാഷ തിരഞ്ഞെടുക്കുക.',
    regContinue: 'തുടരുക',
    regInviteCode: 'Invite Code',
    regInviteCodePlaceholder: 'നിങ്ങളുടെ invite code നൽകുക',
    regErrInviteCode: 'Invite code ആവശ്യമാണ്.',
    regNoInviteCode: 'Invite code ഇല്ലേ? WhatsApp-ൽ ഞങ്ങളെ ബന്ധപ്പെടുക',
    regStepRoleTitle: 'നിങ്ങൾ ആരാണ്?',
    regStepRoleSub: 'രജിസ്ട്രേഷൻ തുടരാൻ ഒരു ഓപ്ഷൻ തിരഞ്ഞെടുക്കുക.',
    regRoleClient: 'Client / Contractor',
    regRoleClientDesc: 'നിങ്ങളുടെ പ്രോജക്റ്റുകൾക്ക് excavator, JCB, crane എന്നിവയും മറ്റ് മെഷീനുകളും ബുക്ക് ചെയ്യുക.',
    regRoleOwner: 'Machine Owner',
    regRoleOwnerDesc: 'നിങ്ങളുടെ ഫ്ലീറ്റ്, വരുമാനം, മെഷീൻ ലഭ്യത എന്നിവ നിയന്ത്രിക്കുക.',
    regRoleOperator: 'Operator',
    regRoleOperatorDesc: 'നിയോഗിച്ച മെഷീനുകൾ ഓടിക്കുക, ദൈനംദിന ലോഗുകൾ നൽകുക.',
    regRoleComingSoon: 'ഉടൻ വരുന്നു — അഡ്മിനെ ബന്ധപ്പെടുക',
    regStepClientTypeTitle: 'ക്ലയന്റ് തരം തിരഞ്ഞെടുക്കുക',
    regStepClientTypeSub: 'നിങ്ങളുടെ ജോലിക്ക് ഏറ്റവും അടുത്ത ഓപ്ഷൻ തിരഞ്ഞെടുക്കുക',
    regTier1Individual: 'വ്യക്തിഗത / ചെറിയ',
    regTier1Commercial: 'വാണിജ്യ പ്രോജക്ട്',
    regTier1Government: 'സർക്കാർ / MSME',
    regSub_individualResidential: 'വ്യക്തിഗത / ആവാസ',
    regSub_farmer: 'കർഷകൻ / കൃഷി',
    regSub_wellDigging: 'കിണർ കുഴിക്കൽ',
    regSub_industrial: 'വ്യാവസായിക / ഫാക്ടറി',
    regSub_roadConstruction: 'റോഡ് നിർമ്മാണം',
    regSub_canalIrrigation: 'കനാൽ / ജലസേചനം',
    regSub_railway: 'റെയിൽവേ റൂട്ട്',
    regSub_reraBuilder: 'RERA ബിൽഡർ',
    regSub_pwdGovernment: 'PWD / സർക്കാർ',
    regSub_msmeCommercial: 'MSME / വാണിജ്യ',
    regOwnerStepTitle: 'ബിസിനസ് എന്റിറ്റി',
    regOwnerStepSub: 'ഉടമയുടെ ബിസിനസ് രജിസ്ട്രേഷൻ തരം തിരഞ്ഞെടുക്കുക',
    regOwnerTier1Individual: 'വ്യക്തിഗത',
    regOwnerTier1Business: 'ബിസിനസ്',
    regOwnerTier1Corporate: 'കോർപ്പറേറ്റ്',
    regOwnerSub_individual: 'വ്യക്തിഗത / കർഷകൻ',
    regOwnerSub_soleProprietor: 'സോൾ പ്രൊപ്രൈറ്റർ',
    regOwnerSub_partnership: 'പാർട്ണർഷിപ്പ് സ്ഥാപനം',
    regOwnerSub_llp: 'LLP',
    regOwnerSub_privateLimited: 'പ്രൈവറ്റ് ലിമിറ്റഡ് (Pvt Ltd)',
    regOwnerSub_opc: 'ഒറ്റ വ്യക്തി കമ്പനി (OPC)',
    regStepAccountTitle: 'അക്കൗണ്ട് വിവരങ്ങൾ',
    regStepAccountSub: 'നിങ്ങളുടെ വിവരങ്ങൾ നൽകുക — 24 മണിക്കൂറിനുള്ളിൽ അഡ്മിൻ അംഗീകാരം.',
    regFullName: 'പൂർണ്ണ പേര്',
    regMobile: 'മൊബൈൽ നമ്പർ (+91)',
    regEmail: 'Email Address',
    regEmailOptional: 'ഓപ്ഷണൽ',
    regGstin: 'GSTIN',
    regPanUpload: 'PAN Card Upload',
    regPanHint: 'Image അല്ലെങ്കിൽ PDF, പരമാവധി 5 MB',
    regPanSelected: 'ഫയൽ തിരഞ്ഞെടുത്തു',
    regKycSection: 'തിരിച്ചറിയൽ രേഖ',
    regPassword: 'പാസ്‌വേഡ്',
    regConfirmPassword: 'പാസ്‌വേഡ് സ്ഥിരീകരിക്കുക',
    regErrFullName: 'പൂർണ്ണ പേര് ആവശ്യമാണ് (കുറഞ്ഞത് 3 അക്ഷരങ്ങൾ, സംഖ്യകൾ ഇല്ല).',
    regErrMobile: 'സാധുവായ 10 അക്ക മൊബൈൽ നമ്പർ ആവശ്യമാണ്.',
    regErrEmail: 'സാധുവായ email നൽകുക അല്ലെങ്കിൽ ശൂന്യമായി വിടുക.',
    regErrGstin: 'Company രജിസ്ട്രേഷന് സാധുവായ GSTIN ആവശ്യമാണ്.',
    regErrPan: 'PAN card അപ്‌ലോഡ് ആവശ്യമാണ് (image അല്ലെങ്കിൽ PDF, പരമാവധി 5 MB).',
    regErrPassword: 'പാസ്‌വേഡിൽ കുറഞ്ഞത് 8 അക്ഷരങ്ങൾ, 1 വലിയ അക്ഷരം, 1 സംഖ്യ, 1 പ്രത്യേക അക്ഷരം വേണം.',
    regErrConfirm: 'പാസ്‌വേഡുകൾ പൊരുത്തപ്പെടുന്നില്ല.',
    regPasswordWeak: 'ദുർബലം',
    regPasswordMedium: 'ഇടത്തരം',
    regPasswordStrong: 'ശക്തമായ',
    regSubmit: 'സൈൻ അപ്പ് ചെയ്യുക',
    regSubmitting: 'സമർപ്പിക്കുന്നു...',
    regSuccessTitle: 'രജിസ്ട്രേഷൻ സമർപ്പിച്ചു',
    regSuccessMessage: 'അഡ്മിൻ അംഗീകാരം തീർപ്പാക്കാത്തത് (24 മണി). അക്കൗണ്ട് സജീവമായാൽ അറിയിക്കും.',
    ownerNextStep1: 'അപേക്ഷ വിജയകരമായി സമർപ്പിച്ചു',
    ownerNextStep2: '24 മണിക്കൂറിനുള്ളിൽ അഡ്മിൻ അവലോകനം',
    ownerNextStep3: 'അംഗീകാരത്തിൽ WhatsApp അറിയിപ്പ്',
    ownerNextStep4: 'ലോഗിൻ → മെഷീൻ രജിസ്റ്റർ → ബാങ്ക് വിവരം → KYC പൂർത്തിയാക്കുക',
    regApprovalWaiting: 'അഡ്മിൻ അംഗീകരിച്ചാൽ ലോഗിൻ ഇവിടെ സ്വയമേവ കാണാം.',
    regApprovalChecking: 'അംഗീകാര നില പരിശോധിക്കുന്നു…',
    regBack: 'പിന്നോട്ട്',
    regNext: 'അടുത്തത്',
    regRegisterFailed: 'രജിസ്ട്രേഷൻ പരാജയപ്പെട്ടു. വീണ്ടും ശ്രമിക്കുക.',
    regPanUploadFailed: 'PAN രേഖ അപ്‌ലോഡ് ചെയ്യാനായില്ല. വീണ്ടും ശ്രമിക്കുക.',
    regKycUploadFailed: 'തിരിച്ചറിയൽ രേഖകൾ അപ്‌ലോഡ് ചെയ്യാനായില്ല. വീണ്ടും ശ്രമിക്കുക.',
    regAppName: 'Development Express',
    regTagline: 'The Gold Standard of Infrastructure',
  },
  ...REGISTER_I18N_REGIONAL,
};

/** Languages without a dedicated register bundle fall back to Hindi then English */
const REGISTER_LANG_FALLBACK = { ur: 'hi' };

const fadeStep = {
  initial: { opacity: 0, x: 20 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -20 },
  transition: { duration: 0.35, ease: [0.22, 1, 0.36, 1] },
};

function normalizePhone(value) {
  const digits = (value || '').toString().replace(/\D/g, '');
  return digits.length > 10 ? digits.slice(-10) : digits;
}

const COMPANY_NAME_HINTS = [
  'pvt', 'private', 'limited', 'ltd', 'llp', 'opc', 'industries', 'industry',
  'enterprise', 'enterprises', 'company', 'corporation', 'corp',
  'constructions', 'construction', 'builders', 'builder', 'trading',
  'associates', 'solutions', 'technologies', 'infra', 'infrastructure',
  'firm', 'and sons', '& sons',
];

const PERSON_NAME_ERROR =
  'Enter your full name as on Aadhaar (First Middle Last). Do not enter a company name here.';
const PERSON_NAME_SCRIPT_ERROR =
  'Please enter your name in English letters only (as on Aadhaar). Devanagari and other scripts are not accepted.';

/** Match server person-name rule: English letters, ≥3 letters, whole-word company block. */
function personNameIssue(value) {
  const trimmed = (value || '').trim().replace(/\s+/g, ' ');
  if (!trimmed) return PERSON_NAME_ERROR;
  if (/\d/.test(trimmed)) return PERSON_NAME_ERROR;
  if (/[^\x00-\x7F]/.test(trimmed) || !/^[A-Za-z\s.'-]+$/.test(trimmed)) {
    return PERSON_NAME_SCRIPT_ERROR;
  }
  if ((trimmed.match(/[A-Za-z]/g) || []).length < 3) return PERSON_NAME_ERROR;
  const lower = trimmed.toLowerCase();
  if (
    COMPANY_NAME_HINTS.some((hint) =>
      new RegExp(`\\b${hint.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i').test(lower),
    )
  ) {
    return PERSON_NAME_ERROR;
  }
  return null;
}

function isValidFullName(value) {
  return personNameIssue(value) == null;
}

function isValidGstin(value) {
  return /^\d{2}[A-Z]{5}\d{4}[A-Z]\d[A-Z]\d$/.test((value || '').trim().toUpperCase());
}

function isValidPan(value) {
  return /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(
    (value || '').trim().toUpperCase(),
  );
}

function isValidCin(value) {
  return /^[LU]\d{5}[A-Z]{2}\d{4}[A-Z]{3}\d{6}$/.test((value || '').trim().toUpperCase());
}

function isValidLlpin(value) {
  return /^[A-Z]{3}-\d{4}$/.test((value || '').trim().toUpperCase());
}

function isValidDin(value) {
  return /^\d{8}$/.test((value || '').trim());
}

function isValidPassword(value) {
  if ((value || '').length < 8) return false;
  if (!/[A-Z]/.test(value)) return false;
  if (!/[0-9]/.test(value)) return false;
  if (!/[^A-Za-z0-9]/.test(value)) return false;
  return true;
}

function getPasswordStrength(password) {
  if (!password) return { level: 'none', score: 0 };
  let score = 0;
  if (password.length >= 6) score += 1;
  if (password.length >= 8) score += 1;
  if (/[A-Z]/.test(password)) score += 1;
  if (/[0-9]/.test(password)) score += 1;
  if (/[^A-Za-z0-9]/.test(password)) score += 1;
  if (password.length >= 12) score += 1;
  if (score <= 2) return { level: 'weak', score };
  if (score <= 4) return { level: 'medium', score };
  return { level: 'strong', score };
}

async function uploadKycDocument(file, phone, docType) {
  const formData = new FormData();
  formData.append(docType, file);
  formData.append('phone', normalizePhone(phone));
  const response = await fetch(`${API_BASE_URL}/api/auth/register/${docType}`, {
    method: 'POST',
    body: formData,
  });
  const result = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(result?.error || `${docType} upload failed`);
  }
  return result.path;
}

function validateKycFile(file, errorMessage) {
  if (!file) return errorMessage;
  if (file.size > MAX_KYC_BYTES) return errorMessage;
  const allowed = KYC_ACCEPT.split(',');
  const typeOk = allowed.some((mime) => file.type === mime)
    || /\.(jpe?g|png|webp|pdf)$/i.test(file.name);
  if (!typeOk) return errorMessage;
  return null;
}

function ProgressDots({ step, total = 4 }) {
  return (
    <div className="mb-6 flex items-center justify-center gap-2">
      {Array.from({ length: total }, (_, i) => i + 1).map((n) => (
        <div
          key={n}
          className={cn(
            'h-2 rounded-full transition-all duration-400',
            step === n ? 'w-8 shadow-[0_0_12px_rgba(201,168,76,0.45)]' : 'w-2',
            step >= n ? 'bg-[#C9A84C]' : 'bg-white/15',
          )}
          style={step === n ? { backgroundColor: GOLD } : undefined}
        />
      ))}
    </div>
  );
}

function SelectionCard({ selected, disabled, onClick, icon: Icon, title, description, badge }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'relative w-full rounded-2xl border p-4 text-left transition-all duration-200',
        'backdrop-blur-md',
        disabled && 'cursor-not-allowed opacity-55',
        selected
          ? 'border-[#C9A84C] bg-[#C9A84C]/10 shadow-[0_0_24px_rgba(201,168,76,0.15)]'
          : 'border-white/10 bg-white/[0.04] hover:border-[#C9A84C]/40 hover:bg-white/[0.06]',
        !disabled && !selected && 'hover:-translate-y-0.5',
      )}
    >
      <div className="flex items-start gap-3">
        <span
          className={cn(
            'flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border',
            selected ? 'border-[#C9A84C]/50 bg-[#C9A84C]/15 text-[#C9A84C]' : 'border-white/10 bg-white/[0.05] text-[#C9A84C]/80',
          )}
        >
          <Icon className="h-5 w-5" strokeWidth={1.75} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <p className="text-sm font-bold text-white">{title}</p>
            {selected && <Check className="h-4 w-4 shrink-0 text-[#C9A84C]" />}
          </div>
          <p className="mt-1 text-xs leading-relaxed text-white/55">{description}</p>
          {badge && (
            <span className="mt-2 inline-block rounded-full border border-white/15 bg-white/[0.06] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white/45">
              {badge}
            </span>
          )}
        </div>
      </div>
    </button>
  );
}

const RegisterPage = ({ fixedRole = null }) => {
  const { lang, setLang, LANGUAGES, t: tBase } = useLanguage();
  const portalRole = fixedRole === 'client' || fixedRole === 'owner' ? fixedRole : null;
  const t = useCallback(
    (key) => {
      const bundle = REGISTER_I18N[lang]
        || REGISTER_I18N[REGISTER_LANG_FALLBACK[lang]]
        || REGISTER_I18N.hi;
      return bundle?.[key] || REGISTER_I18N.en[key] || tBase(key);
    },
    [lang, tBase],
  );

  const [step, setStep] = useState(1);
  const [success, setSuccess] = useState(false);
  const [inviteCode, setInviteCode] = useState('');
  const [role, setRole] = useState(portalRole || 'client');
  const [profilePhoto, setProfilePhoto] = useState('');
  const [profilePhotoFile, setProfilePhotoFile] = useState(null);
  const [profilePhotoError, setProfilePhotoError] = useState('');
  const [tier1, setTier1] = useState('');
  const [tier2, setTier2] = useState('');
  const [ownerTier1, setOwnerTier1] = useState('');
  const [ownerTier2, setOwnerTier2] = useState('');
  const [cin, setCin] = useState('');
  const [din, setDin] = useState('');
  const [workOrderNumber, setWorkOrderNumber] = useState('');
  const ownerEntityType = ownerTier2 || '';
  const isOwnerReg = portalRole === 'owner';
  const ownerNeedsCompany = isOwnerReg
    && ['partnership', 'llp', 'private_limited', 'opc'].includes(ownerEntityType);
  const ownerGstMandatory = ownerNeedsCompany;
  const ownerShowsGstCert = ownerGstMandatory;
  const ownerShowsCin = isOwnerReg && ['llp', 'private_limited', 'opc'].includes(ownerEntityType);
  const ownerShowsDin = isOwnerReg && ['private_limited', 'opc'].includes(ownerEntityType);
  const showCompanyName = isOwnerReg ? ownerNeedsCompany : false;
  const showGstin = isOwnerReg;
  const showGstCert = isOwnerReg ? ownerShowsGstCert : false;
  const businessType = ['commercial_project', 'government_msme'].includes(tier1)
    ? 'company'
    : 'individual';
  const [form, setForm] = useState({
    fullName: '',
    mobile: '',
    email: '',
    companyName: '',
    gstin: '',
    udyamNumber: '',
    panNumber: '',
    password: '',
    confirmPassword: '',
  });
  const [panFile, setPanFile] = useState(null);
  const [gstCertFile, setGstCertFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [dpdpAccepted, setDpdpAccepted] = useState(false);
  const [dpdpOpen, setDpdpOpen] = useState(false);
  const [registeredAccount, setRegisteredAccount] = useState(null);
  const [isApproved, setIsApproved] = useState(false);
  const [isRejected, setIsRejected] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [approvalChecking, setApprovalChecking] = useState(false);

  const loginPath = (registeredAccount?.role || portalRole || role) === 'owner'
    ? '/login/owner'
    : '/login/client';

  const checkApprovalStatus = useCallback(async (account) => {
    if (!account?.phone) return false;
    setApprovalChecking(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/registration-status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: account.phone, role: account.role }),
      });
      const result = await response.json().catch(() => ({}));
      if (response.ok && result.approved) {
        setIsApproved(true);
        setIsRejected(false);
        setRejectionReason('');
        sessionStorage.removeItem(REG_PENDING_STORAGE_KEY);
        return true;
      }
      if (response.ok && (result.rejected || result.status === 'rejected')) {
        setIsRejected(true);
        setIsApproved(false);
        setRejectionReason(
          (result.rejectionReason || result.rejection_reason || '').toString().trim(),
        );
        sessionStorage.removeItem(REG_PENDING_STORAGE_KEY);
        return true;
      }
      return false;
    } catch (_err) {
      return false;
    } finally {
      setApprovalChecking(false);
    }
  }, []);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(REG_PENDING_STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed?.phone && parsed?.role) {
        setRegisteredAccount(parsed);
        setSuccess(true);
      }
    } catch (_err) {
      sessionStorage.removeItem(REG_PENDING_STORAGE_KEY);
    }
  }, []);

  useEffect(() => {
    if (!registeredAccount || isApproved || isRejected) return undefined;
    let cancelled = false;
    const run = async () => {
      if (cancelled) return;
      await checkApprovalStatus(registeredAccount);
    };
    run();
    const timer = setInterval(run, 20000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [registeredAccount, isApproved, isRejected, checkApprovalStatus]);

  const passwordStrength = useMemo(() => getPasswordStrength(form.password), [form.password]);

  const strengthLabel = useMemo(() => {
    if (passwordStrength.level === 'weak') return t('regPasswordWeak');
    if (passwordStrength.level === 'medium') return t('regPasswordMedium');
    if (passwordStrength.level === 'strong') return t('regPasswordStrong');
    return '';
  }, [passwordStrength.level, t]);

  const strengthWidth = useMemo(() => {
    if (passwordStrength.level === 'none') return '0%';
    if (passwordStrength.level === 'weak') return '33%';
    if (passwordStrength.level === 'medium') return '66%';
    return '100%';
  }, [passwordStrength.level]);

  const strengthColor = useMemo(() => {
    if (passwordStrength.level === 'weak') return '#ef4444';
    if (passwordStrength.level === 'medium') return '#f59e0b';
    if (passwordStrength.level === 'strong') return '#22c55e';
    return 'transparent';
  }, [passwordStrength.level]);

  const goBack = () => {
    if (step === 1) {
      window.location.assign(
        'https://developmentexpress.in',
      );
      return;
    }
    if (step === 3 && portalRole) {
      setStep(1);
      return;
    }
    setStep((s) => s - 1);
  };

  const progressTotal = portalRole ? 3 : 4;
  const progressStep = portalRole && step >= 3 ? step - 1 : step;

  const goNext = () => {
    if (step === 1) {
      if (!inviteCode.trim()) {
        setErrors((prev) => ({ ...prev, inviteCode: t('regErrInviteCode') }));
        return;
      }
      setErrors((prev) => {
        const next = { ...prev };
        delete next.inviteCode;
        return next;
      });
    }
    if (step === 2 && !role) return;
    if (step === 3 && portalRole === 'owner' && !ownerTier2) return;
    if (step === 3 && portalRole !== 'owner' && !tier2) return;
    if (step === 1 && portalRole) {
      setStep(3);
      return;
    }
    setStep((s) => Math.min(4, s + 1));
  };

  const validateAccountForm = () => {
    const nextErrors = {};
    const nameIssue = personNameIssue(form.fullName);
    if (nameIssue) nextErrors.fullName = nameIssue;
    if (!/^[6-9]\d{9}$/.test(normalizePhone(form.mobile))) nextErrors.mobile = t('regErrMobile');
    const emailTrim = form.email.trim();
    if (emailTrim && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrim.toLowerCase())) {
      nextErrors.email = t('regErrEmail');
    }
    const isOwnerReg = portalRole === 'owner';
    if (isOwnerReg) {
      const needsCompanyName = ['partnership', 'llp', 'private_limited', 'opc'].includes(ownerEntityType);
      const gstMandatory = ['partnership', 'llp', 'private_limited', 'opc'].includes(ownerEntityType);
      const gstCertShown = gstMandatory;
      const needsCin = ['llp', 'private_limited', 'opc'].includes(ownerEntityType);
      const needsDin = ['private_limited', 'opc'].includes(ownerEntityType);
      if (needsCompanyName && !form.companyName?.trim()) {
        nextErrors.companyName = ownerEntityType === 'partnership'
          ? 'Partnership firm name required'
          : ownerEntityType === 'llp'
            ? 'LLP name required'
            : 'Company name required';
      }
      if (gstMandatory && !isValidGstin(form.gstin)) nextErrors.gstin = t('regErrGstin');
      if (!gstMandatory && form.gstin.trim() && !isValidGstin(form.gstin)) {
        nextErrors.gstin = t('regErrGstin');
      }
      if (gstCertShown && !gstCertFile) {
        nextErrors.gstCert = 'GST certificate required';
      }
      if (needsCin) {
        if (ownerEntityType === 'llp') {
          if (!isValidLlpin(cin)) nextErrors.cin = t('regErrLlpin');
        } else if (!isValidCin(cin)) {
          nextErrors.cin = t('regErrCin');
        }
      }
      if (needsDin && !isValidDin(din)) {
        nextErrors.din = t('regErrDin');
      }
    } else if (businessType === 'company') {
      if (!form.companyName?.trim()) {
        nextErrors.companyName = 'Company name required';
      }
      if (!isValidGstin(form.gstin)) nextErrors.gstin = t('regErrGstin');
      if (!gstCertFile) {
        nextErrors.gstCert = 'GST certificate required';
      }
    }
    if (!panFile) {
      nextErrors.pan = t('regErrPan');
    } else {
      const panErr = validateKycFile(panFile, t('regErrPan'));
      if (panErr) nextErrors.pan = panErr;
    }
    if (!isValidPan(form.panNumber)) {
      nextErrors.panNumber =
        'Valid PAN number required (e.g. ABCDE1234F)';
    }
    if (!isValidPassword(form.password)) nextErrors.password = t('regErrPassword');
    if (form.password !== form.confirmPassword) nextErrors.confirmPassword = t('regErrConfirm');
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!termsAccepted || !dpdpAccepted) {
      setSubmitError(!termsAccepted
        ? (t('termsRequired') || tBase('termsRequired'))
        : t('regDpdpRequired'));
      return;
    }
    if (!validateAccountForm()) return;
    setLoading(true);
    setSubmitError('');
    try {
      const panDocumentPath = await uploadKycDocument(panFile, form.mobile, 'pan');
      const registerRole = portalRole === 'owner' || role === 'owner' ? 'owner' : 'client';
      const ownerNeedsGstCert = registerRole === 'owner'
        && ['partnership', 'llp', 'private_limited', 'opc'].includes(ownerEntityType);
      let gstCertPath = '';
      if ((businessType === 'company' || ownerNeedsGstCert) && gstCertFile) {
        gstCertPath = await uploadKycDocument(
          gstCertFile, form.mobile, 'gst-certificate',
        );
      }
      let profilePhotoPath = '';
      if (profilePhotoFile) {
        profilePhotoPath = await uploadRegistrationProfilePhoto(profilePhotoFile, form.mobile);
      }
      const cleanPhone = normalizePhone(form.mobile);
      const cleanEmail = form.email.trim().toLowerCase();
      const cleanName = form.fullName.trim();
      const ownerHasCompany = registerRole === 'owner'
        && ['partnership', 'llp', 'private_limited', 'opc'].includes(ownerEntityType);
      const cleanGstin = registerRole === 'owner'
        ? form.gstin.trim().toUpperCase()
        : (businessType === 'company' ? form.gstin.trim().toUpperCase() : '');
      const company = registerRole === 'owner'
        ? (ownerHasCompany ? (form.companyName?.trim() || cleanName) : `Individual — ${cleanName}`)
        : (businessType === 'company'
          ? (form.companyName?.trim() || cleanName)
          : `Individual — ${cleanName}`);

      const registerBody = {
          name: cleanName,
          company,
          email: cleanEmail || undefined,
          phone: cleanPhone,
          gstin: cleanGstin,
          udyam_number: form.udyamNumber?.trim() || undefined,
          client_type: tier2,
          password: form.password,
          panNumber: form.panNumber.trim().toUpperCase(),
          panDocumentPath,
          gstCertificatePath: gstCertPath || undefined,
          profilePhotoPath: profilePhotoPath || undefined,
          role: registerRole,
          invite_code: inviteCode.trim().toUpperCase(),
          terms_accepted: true,
          dpdp_consent: true,
          work_order_number: workOrderNumber || null,
      };
      if (registerRole === 'owner') {
        registerBody.owner_entity_type = ownerEntityType || 'sole_proprietor';
        registerBody.cin = cin.trim() || undefined;
        registerBody.din = din.trim() || undefined;
      }

      const response = await fetch(`${API_BASE_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(registerBody),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        setSubmitError(result?.error || t('regRegisterFailed'));
        return;
      }
      if (profilePhoto) saveProfilePhoto(null, profilePhoto, registerRole);
      const account = { phone: cleanPhone, role: registerRole };
      setRegisteredAccount(account);
      sessionStorage.setItem(REG_PENDING_STORAGE_KEY, JSON.stringify(account));
      setIsApproved(false);
      setSuccess(true);
    } catch (err) {
      const msg = String(err?.message || '').toLowerCase();
      if (msg.includes('pan') || msg.includes('storage') || msg.includes('bucket') || msg.includes('upload')) {
        setSubmitError(msg.includes('pan') ? t('regPanUploadFailed') : t('regKycUploadFailed'));
      } else {
        setSubmitError(err?.message || t('regRegisterFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center px-4 pb-[env(safe-area-inset-bottom,0px)]"
        style={{ backgroundColor: 'rgba(5, 10, 18, 0.92)' }}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.94, y: 16 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          className="w-full max-w-md rounded-3xl border p-8 text-center shadow-[0_32px_80px_rgba(0,0,0,0.55)] backdrop-blur-xl"
          style={{ borderColor: `${GOLD}55`, backgroundColor: `${NAVY}ee` }}
        >
          <div
            className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full"
            style={{ backgroundColor: `${GOLD}22`, border: `1px solid ${GOLD}44` }}
          >
            <Sparkles className="h-8 w-8" style={{ color: GOLD }} />
          </div>
          <h2 className="text-xl font-bold text-white">
            {isRejected ? 'Registration not approved' : t('regSuccessTitle')}
          </h2>
          {isRejected ? (
            <>
              <p className="mt-3 text-sm leading-relaxed text-white/65">
                Your registration could not be approved at this time.
              </p>
              {rejectionReason ? (
                <p className="mt-3 rounded-xl border border-red-400/30 bg-red-500/10 px-3 py-2 text-left text-sm text-red-200">
                  <span className="font-semibold text-red-100">Reason: </span>
                  {rejectionReason}
                </p>
              ) : null}
              <p className="mt-3 text-xs leading-relaxed text-white/50">
                Need help? WhatsApp +91-8408000084
              </p>
              <Button
                asChild
                className="mt-8 h-12 w-full rounded-2xl border-0 text-sm font-extrabold text-[#0D1B2A] shadow-[0_12px_32px_rgba(201,168,76,0.32)]"
                style={{ background: `linear-gradient(90deg, #a07830, ${GOLD}, #a07830)` }}
              >
                <Link to={loginPath}>
                  {tBase('marketingNavLogin')}
                </Link>
              </Button>
            </>
          ) : (
            <>
              <p className="mt-3 text-sm leading-relaxed text-white/65">{t('regSuccessMessage')}</p>
              {(registeredAccount?.role || portalRole || role) === 'owner' && !isRejected && (
                <div
                  className="mt-5 rounded-2xl border p-4 text-left"
                  style={{ borderColor: `${GOLD}44`, backgroundColor: 'rgba(201,168,76,0.08)' }}
                >
                  {[
                    { key: 'ownerNextStep1', Icon: Check, done: true },
                    { key: 'ownerNextStep2', Icon: Clock, done: false },
                    { key: 'ownerNextStep3', Icon: Bell, done: false },
                    { key: 'ownerNextStep4', Icon: ListOrdered, done: false },
                  ].map((stepItem, idx) => (
                    <div
                      key={stepItem.key}
                      className={cn('flex items-start gap-3', idx > 0 && 'mt-3 pt-3 border-t border-white/10')}
                    >
                      <span
                        className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full"
                        style={{
                          backgroundColor: stepItem.done ? 'rgba(34,197,94,0.2)' : `${GOLD}22`,
                          border: `1px solid ${stepItem.done ? 'rgba(34,197,94,0.5)' : `${GOLD}55`}`,
                          color: stepItem.done ? '#4ade80' : GOLD,
                        }}
                      >
                        <stepItem.Icon className="h-3.5 w-3.5" />
                      </span>
                      <p className="text-xs leading-relaxed text-white/75 sm:text-sm">{t(stepItem.key)}</p>
                    </div>
                  ))}
                </div>
              )}
              {isApproved ? (
                <Button
                  asChild
                  className="mt-8 h-12 w-full rounded-2xl border-0 text-sm font-extrabold text-[#0D1B2A] shadow-[0_12px_32px_rgba(201,168,76,0.32)]"
                  style={{ background: `linear-gradient(90deg, #a07830, ${GOLD}, #a07830)` }}
                >
                  <Link to={loginPath}>
                    {tBase('marketingNavLogin')}
                  </Link>
                </Button>
              ) : (
                <div className="mt-8 space-y-2">
                  <p className="text-xs leading-relaxed text-white/50">{t('regApprovalWaiting')}</p>
                  {approvalChecking && (
                    <p className="text-[10px] text-white/35">{t('regApprovalChecking')}</p>
                  )}
                </div>
              )}
            </>
          )}
        </motion.div>
      </div>
    );
  }

  return (
    <div className="relative min-h-dvh overflow-x-hidden font-sans text-white" style={{ backgroundColor: NAVY }}>
      <div
        className="pointer-events-none fixed inset-0 bg-[linear-gradient(rgba(201,168,76,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(201,168,76,0.03)_1px,transparent_1px)] bg-[size:48px_48px]"
        aria-hidden
      />
      <div
        className="pointer-events-none fixed inset-0 bg-[radial-gradient(ellipse_at_50%_0%,rgba(201,168,76,0.1),transparent_55%)]"
        aria-hidden
      />

      <header className="relative z-20 flex items-center justify-between gap-3 px-4 pb-2 pt-[calc(env(safe-area-inset-top,0px)+12px)] md:px-8">
        <div className="flex items-center gap-3">
          {step !== 1 && (
            <button
              type="button"
              onClick={goBack}
              className="flex h-10 w-10 items-center justify-center rounded-xl border border-white/10 bg-white/[0.04] text-[#C9A84C] transition-colors hover:bg-white/[0.08]"
              aria-label={t('regBack')}
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
          )}
          <div>
            <p className="text-sm font-extrabold tracking-wide" style={{ color: GOLD }}>
              {t('regAppName')}
            </p>
            <p className="hidden text-[10px] uppercase tracking-[0.18em] text-white/40 sm:block">{t('regTagline')}</p>
          </div>
        </div>
        <LanguageSelector compact />
      </header>

      <main className="relative z-10 mx-auto w-full max-w-lg px-4 pb-[calc(3rem+env(safe-area-inset-bottom,0px))] pt-4 md:max-w-xl">
        <ProgressDots step={progressStep} total={progressTotal} />

        <div
          className="rounded-3xl border p-5 shadow-[0_24px_64px_rgba(0,0,0,0.45)] backdrop-blur-xl sm:p-7"
          style={{ borderColor: `${GOLD}33`, backgroundColor: 'rgba(255,255,255,0.04)' }}
        >
          <AnimatePresence mode="wait">
            {step === 1 && (
              <motion.div key="step-1" {...fadeStep}>
                <h1 className="text-xl font-bold text-white sm:text-2xl">{t('regHello')}</h1>
                <p className="mt-2 text-sm text-white/55">{t('regSelectLanguage')}</p>
                <div className="mt-6 space-y-1.5">
                  <Label className="text-white/70">
                    {t('regInviteCode')}
                    <span className="ml-0.5 text-red-400">*</span>
                  </Label>
                  <Input
                    className="h-11 rounded-xl border-white/15 bg-[#0a1628]/80 text-base uppercase tracking-wider text-white sm:text-sm sm:tracking-[0.18em]"
                    value={inviteCode}
                    onChange={(e) => {
                      setInviteCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8));
                      if (errors.inviteCode) {
                        setErrors((prev) => {
                          const next = { ...prev };
                          delete next.inviteCode;
                          return next;
                        });
                      }
                    }}
                    placeholder={t('regInviteCodePlaceholder')}
                    autoComplete="off"
                    spellCheck={false}
                  />
                  {errors.inviteCode && <p className="text-xs text-red-400">{errors.inviteCode}</p>}
                </div>
                <div className="mt-6 grid max-h-[min(280px,38dvh)] grid-cols-2 gap-2 overflow-y-auto overscroll-contain sm:max-h-[min(360px,50dvh)] sm:grid-cols-3">
                  {LANGUAGES.map((l) => (
                    <button
                      key={l.code}
                      type="button"
                      onClick={() => setLang(l.code)}
                      className={cn(
                        'rounded-xl border px-2 py-2.5 text-center transition-all sm:px-3 sm:py-3',
                        lang === l.code
                          ? 'border-[#C9A84C] bg-[#C9A84C]/12 text-[#C9A84C]'
                          : 'border-white/10 bg-white/[0.03] text-white/80 hover:border-[#C9A84C]/35',
                      )}
                    >
                      <p className="text-xs font-bold sm:text-sm">{l.native}</p>
                    </button>
                  ))}
                </div>
                <Button
                  type="button"
                  className="mt-8 h-12 w-full rounded-2xl border-0 text-sm font-extrabold text-[#0D1B2A]"
                  style={{ background: `linear-gradient(90deg, #a07830, ${GOLD}, #a07830)` }}
                  onClick={goNext}
                >
                  {t('regContinue')}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </motion.div>
            )}

            {step === 2 && !portalRole && (
              <motion.div key="step-2" {...fadeStep}>
                <h1 className="text-xl font-bold text-white">{t('regStepRoleTitle')}</h1>
                <p className="mt-2 text-sm text-white/55">{t('regStepRoleSub')}</p>
                <div className="mt-6 space-y-3">
                  <SelectionCard
                    selected={role === 'client'}
                    onClick={() => setRole('client')}
                    icon={HardHat}
                    title={t('regRoleClient')}
                    description={t('regRoleClientDesc')}
                  />
                  <SelectionCard
                    selected={role === 'owner'}
                    onClick={() => setRole('owner')}
                    icon={Building2}
                    title={t('regRoleOwner')}
                    description={t('regRoleOwnerDesc')}
                  />
                  <SelectionCard
                    selected={false}
                    disabled
                    icon={Wrench}
                    title={t('regRoleOperator')}
                    description={t('regRoleOperatorDesc')}
                    badge={t('regRoleComingSoon')}
                  />
                </div>
                <Button
                  type="button"
                  disabled={!role}
                  className="mt-8 h-12 w-full rounded-2xl border-0 text-sm font-extrabold text-[#0D1B2A] disabled:opacity-50"
                  style={{ background: `linear-gradient(90deg, #a07830, ${GOLD}, #a07830)` }}
                  onClick={goNext}
                >
                  {t('regNext')}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </motion.div>
            )}

            {step === 3 && portalRole === 'owner' && (
              <motion.div key="step-3-owner" {...fadeStep}>
                <h1 className="text-xl font-bold text-white">{t('regOwnerStepTitle')}</h1>
                <p className="mt-2 text-sm text-white/55">{t('regOwnerStepSub')}</p>
                <div className="mt-6 flex w-full gap-2">
                  {OWNER_ENTITY_TIER1.map((opt) => {
                    const selected = ownerTier1 === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => {
                          setOwnerTier1(opt.value);
                          setOwnerTier2('');
                        }}
                        className={cn(
                          'flex min-w-0 flex-1 cursor-pointer flex-col items-center justify-center gap-1 rounded-2xl px-2 py-3 text-center text-[11px] font-semibold leading-tight outline-none sm:text-xs',
                          selected
                            ? 'border-2 border-[#C9A84C] bg-[#C9A84C]/10 text-[#C9A84C]'
                            : 'border border-white/10 bg-white/[0.04] text-white/80 hover:border-[#C9A84C]/40 hover:bg-white/[0.06]',
                        )}
                      >
                        <span className="text-lg" aria-hidden="true">{opt.icon}</span>
                        <span>{t(opt.labelKey)}</span>
                      </button>
                    );
                  })}
                </div>
                {ownerTier1 ? (
                  <div className="mt-4 grid grid-cols-2 gap-2">
                    {(OWNER_ENTITY_TIER2[ownerTier1] || []).map((opt) => {
                      const selected = ownerTier2 === opt.value;
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => setOwnerTier2(opt.value)}
                          className={cn(
                            'flex cursor-pointer items-center gap-2 rounded-2xl px-3 py-3 text-left text-xs font-semibold outline-none',
                            selected
                              ? 'border-2 border-[#C9A84C] bg-[#C9A84C]/10 text-[#C9A84C]'
                              : 'border border-white/10 bg-white/[0.04] text-white/90 hover:border-[#C9A84C]/40 hover:bg-white/[0.06]',
                          )}
                        >
                          <span aria-hidden="true">{opt.icon}</span>
                          <span>{t(opt.labelKey)}</span>
                        </button>
                      );
                    })}
                  </div>
                ) : null}
                <Button
                  type="button"
                  disabled={!ownerTier2}
                  className="mt-8 h-12 w-full rounded-2xl border-0 text-sm font-extrabold text-[#0D1B2A] disabled:opacity-50"
                  style={{ background: `linear-gradient(90deg, #a07830, ${GOLD}, #a07830)` }}
                  onClick={goNext}
                >
                  {t('regNext')}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </motion.div>
            )}

            {step === 3 && portalRole !== 'owner' && (
              <motion.div key="step-3" {...fadeStep}>
                <h1 className="text-xl font-bold text-white">{t('regStepClientTypeTitle')}</h1>
                <p className="mt-2 text-sm text-white/55">{t('regStepClientTypeSub')}</p>
                <div className="mt-6 flex w-full gap-2">
                  {CLIENT_TYPE_TIER1.map((opt) => {
                    const selected = tier1 === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => {
                          setTier1(opt.value);
                          setTier2('');
                        }}
                        className={cn(
                          'flex min-w-0 flex-1 cursor-pointer flex-col items-center justify-center gap-1 rounded-2xl px-2 py-3 text-center text-[11px] font-semibold leading-tight outline-none sm:text-xs',
                          selected
                            ? 'border-2 border-[#C9A84C] bg-[#C9A84C]/10 text-[#C9A84C]'
                            : 'border border-white/10 bg-white/[0.04] text-white/80 hover:border-[#C9A84C]/40 hover:bg-white/[0.06]',
                        )}
                      >
                        <span className="text-lg" aria-hidden="true">{opt.icon}</span>
                        <span>{t(opt.labelKey)}</span>
                      </button>
                    );
                  })}
                </div>
                {tier1 ? (
                  <div className="mt-4 grid grid-cols-2 gap-2">
                    {(CLIENT_TYPE_TIER2[tier1] || []).map((opt) => {
                      const selected = tier2 === opt.value;
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => setTier2(opt.value)}
                          className={cn(
                            'flex cursor-pointer items-center gap-2 rounded-2xl px-3 py-3 text-left text-xs font-semibold outline-none',
                            selected
                              ? 'border-2 border-[#C9A84C] bg-[#C9A84C]/10 text-[#C9A84C]'
                              : 'border border-white/10 bg-white/[0.04] text-white/90 hover:border-[#C9A84C]/40 hover:bg-white/[0.06]',
                          )}
                        >
                          <span aria-hidden="true">{opt.icon}</span>
                          <span>{t(opt.labelKey)}</span>
                        </button>
                      );
                    })}
                  </div>
                ) : null}
                <Button
                  type="button"
                  disabled={!tier2}
                  className="mt-8 h-12 w-full rounded-2xl border-0 text-sm font-extrabold text-[#0D1B2A] disabled:opacity-50"
                  style={{ background: `linear-gradient(90deg, #a07830, ${GOLD}, #a07830)` }}
                  onClick={goNext}
                >
                  {t('regNext')}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </motion.div>
            )}

            {step === 4 && (
              <motion.div key="step-4" {...fadeStep}>
                <h1 className="text-xl font-bold text-white">
                  {portalRole === 'owner' ? 'Owner registration' : t('regStepAccountTitle')}
                </h1>
                <p className="mt-2 text-sm text-white/55">
                  {portalRole === 'owner'
                    ? 'Fleet details & KYC — admin assigns owner access after verification.'
                    : t('regStepAccountSub')}
                </p>

                <div className="mt-6 rounded-2xl border border-white/10 bg-white/[0.02] p-4">
                  <ProfilePhotoUpload
                    value={profilePhoto}
                    onChange={(url, meta) => {
                      setProfilePhoto(url);
                      setProfilePhotoFile(meta?.file || null);
                      setProfilePhotoError(meta?.error || '');
                    }}
                    name={form.fullName}
                    label={portalRole === 'owner' ? 'Owner profile photo' : 'Your profile photo'}
                    hint="Optional — shown in your portal after login"
                    size="md"
                  />
                  {profilePhotoError && (
                    <p className="mt-2 text-xs text-red-400">{profilePhotoError}</p>
                  )}
                </div>

                <div className="mt-6 space-y-4">
                  <div className="space-y-1.5">
                    <Label className="text-white/70">
                      {t('regFullName')}
                      <span className="ml-0.5 text-red-400">*</span>
                    </Label>
                    <Input
                      className="h-11 rounded-xl border-white/15 bg-[#0a1628]/80 text-base text-white sm:text-sm"
                      value={form.fullName}
                      onChange={(e) => setForm((p) => ({ ...p, fullName: e.target.value }))}
                      placeholder="Rajesh Patil"
                    />
                    {errors.fullName && <p className="text-xs text-red-400">{errors.fullName}</p>}
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-white/70">
                      {t('regMobile')}
                      <span className="ml-0.5 text-red-400">*</span>
                    </Label>
                    <div className="flex gap-2">
                      <span className="flex h-11 items-center rounded-xl border border-white/15 bg-white/[0.04] px-3 text-sm text-white/60">
                        +91
                      </span>
                      <Input
                        className="h-11 flex-1 rounded-xl border-white/15 bg-[#0a1628]/80 text-base text-white sm:text-sm"
                        inputMode="numeric"
                        maxLength={10}
                        value={form.mobile}
                        onChange={(e) => setForm((p) => ({ ...p, mobile: e.target.value.replace(/\D/g, '').slice(0, 10) }))}
                        placeholder="9876543210"
                      />
                    </div>
                    {errors.mobile && <p className="text-xs text-red-400">{errors.mobile}</p>}
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-white/70">
                      {t('regEmail')}
                      <span className="ml-1 text-[10px] font-normal text-white/35">({t('regEmailOptional')})</span>
                    </Label>
                    <Input
                      className="h-11 rounded-xl border-white/15 bg-[#0a1628]/80 text-base text-white sm:text-sm"
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                      placeholder="you@company.com"
                    />
                    {errors.email && <p className="text-xs text-red-400">{errors.email}</p>}
                  </div>

                  {((portalRole !== 'owner' && businessType === 'company') || showCompanyName) && (
                    <div className="space-y-1.5">
                      <Label className="text-white/70">
                        {ownerEntityType === 'partnership'
                          ? 'Partnership firm name'
                          : ownerEntityType === 'llp'
                            ? 'LLP name'
                            : 'Company Full Name'}
                        <span className="ml-0.5 text-red-400">*</span>
                      </Label>
                      <Input
                        className="h-11 rounded-xl border-white/15 bg-[#0a1628]/80 text-base text-white sm:text-sm"
                        value={form.companyName}
                        onChange={(e) => setForm((p) => ({
                          ...p, companyName: e.target.value,
                        }))}
                        placeholder="ABC Construction Pvt Ltd"
                      />
                      {errors.companyName && (
                        <p className="text-xs text-red-400">
                          {errors.companyName}
                        </p>
                      )}
                    </div>
                  )}

                  {((portalRole !== 'owner' && businessType === 'company') || showGstin) && (
                    <div className="space-y-1.5">
                      <Label className="text-white/70">
                        {t('regGstin')}
                        {((portalRole !== 'owner' && businessType === 'company') || ownerGstMandatory) ? (
                          <span className="ml-0.5 text-red-400">*</span>
                        ) : (
                          <span className="ml-1 text-[10px] font-normal text-white/35">(optional)</span>
                        )}
                      </Label>
                      <Input
                        className="h-11 rounded-xl border-white/15 bg-[#0a1628]/80 text-base uppercase text-white sm:text-sm"
                        value={form.gstin}
                        onChange={(e) => setForm((p) => ({ ...p, gstin: e.target.value.toUpperCase() }))}
                        placeholder="27AABCP1234A1Z5"
                      />
                      {errors.gstin && <p className="text-xs text-red-400">{errors.gstin}</p>}
                    </div>
                  )}

                  <div className="space-y-1.5">
                    <Label className="text-white/70">
                      Udyam Registration Number
                      <span className="ml-1 text-[10px] font-normal text-white/35">(optional)</span>
                    </Label>
                    <Input
                      className="h-11 rounded-xl border-white/15 bg-[#0a1628]/80 text-base uppercase text-white sm:text-sm"
                      value={form.udyamNumber}
                      onChange={(e) => setForm((p) => ({
                        ...p,
                        udyamNumber: e.target.value.toUpperCase(),
                      }))}
                      placeholder="UDYAM-MH-XX-XXXXXXX"
                    />
                    <p className="text-[10px] text-white/40">
                      MSMED Act 2026 — helps claim delayed-payment &amp; ODR remedies if you are an MSME.
                    </p>
                  </div>

                  {((portalRole !== 'owner' && businessType === 'company') || showGstCert) && (
                    <div className="space-y-1.5">
                      <Label className="text-white/70">
                        GST Certificate
                        <span className="ml-0.5 text-red-400">*</span>
                      </Label>
                      <label className="flex cursor-pointer flex-col items-center justify-center rounded-xl border border-dashed border-[#C9A84C]/30 bg-white/[0.02] p-4 transition hover:border-[#C9A84C]/60">
                        <Upload className="mb-2 h-5 w-5 text-[#C9A84C]" />
                        <span className="text-xs text-white/55">
                          {gstCertFile
                            ? gstCertFile.name
                            : 'Upload GST Certificate (PDF/Image)'}
                        </span>
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/webp,application/pdf"
                          className="hidden"
                          onChange={(e) => {
                            setGstCertFile(e.target.files?.[0] || null);
                            setErrors((p) => ({ ...p, gstCert: undefined }));
                          }}
                        />
                      </label>
                      {errors.gstCert && (
                        <p className="text-xs text-red-400">
                          {errors.gstCert}
                        </p>
                      )}
                    </div>
                  )}

                  {businessType === 'company' && tier1 === 'government_msme' && (
                    <div className="space-y-1.5">
                      <Label className="text-white/70">
                        Work Order / Tender Reference
                        <span className="ml-1 text-[10px] font-normal text-white/35">(optional)</span>
                      </Label>
                      <Input
                        className="h-11 rounded-xl border-white/15 bg-[#0a1628]/80 text-base text-white sm:text-sm"
                        value={workOrderNumber}
                        onChange={(e) => setWorkOrderNumber(e.target.value)}
                        placeholder="e.g. PWD/2026/WO-1234"
                      />
                    </div>
                  )}

                  {ownerShowsCin && (
                    <div className="space-y-1.5">
                      <Label className="text-white/70">
                        {ownerEntityType === 'llp'
                          ? 'LLP Registration No. (LLPIN)'
                          : 'Company Identification No. (CIN)'}
                        <span className="ml-0.5 text-red-400">*</span>
                      </Label>
                      <Input
                        className="h-11 rounded-xl border-white/15 bg-[#0a1628]/80 text-base uppercase text-white sm:text-sm"
                        value={cin}
                        onChange={(e) => setCin(e.target.value.toUpperCase())}
                        placeholder={ownerEntityType === 'llp' ? 'AAX-1234' : 'U12345MH2020PTC123456'}
                      />
                      {errors.cin && <p className="text-xs text-red-400">{errors.cin}</p>}
                    </div>
                  )}

                  {ownerShowsDin && (
                    <div className="space-y-1.5">
                      <Label className="text-white/70">
                        Director Identification No. (DIN)
                        <span className="ml-0.5 text-red-400">*</span>
                      </Label>
                      <Input
                        className="h-11 rounded-xl border-white/15 bg-[#0a1628]/80 text-base uppercase text-white sm:text-sm"
                        value={din}
                        onChange={(e) => setDin(e.target.value.toUpperCase())}
                        placeholder="12345678"
                      />
                      {errors.din && <p className="text-xs text-red-400">{errors.din}</p>}
                    </div>
                  )}

                  <div className="space-y-3 rounded-2xl border border-white/10 bg-white/[0.02] p-4">
                    <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#C9A84C]/75">
                      {t('regKycSection')}
                    </p>

                    <div className="space-y-1.5">
                      <Label className="text-white/70">
                        {ownerEntityType === 'partnership' ? 'Firm PAN' : 'PAN Number'}
                        <span className="ml-0.5 text-red-400">*</span>
                      </Label>
                      <Input
                        value={form.panNumber}
                        onChange={(e) => setForm((p) => ({
                          ...p,
                          panNumber: e.target.value
                            .toUpperCase()
                            .replace(/[^A-Z0-9]/g, '')
                            .slice(0, 10),
                        }))}
                        placeholder="ABCDE1234F"
                        maxLength={10}
                      />
                      {errors.panNumber && (
                        <p className="text-xs text-red-400">
                          {errors.panNumber}
                        </p>
                      )}
                    </div>

                    <div className="space-y-1.5">
                      <Label className="text-white/70">
                        {t('regPanUpload')}
                        <span className="ml-0.5 text-red-400">*</span>
                      </Label>
                      <label
                        className={cn(
                          'flex cursor-pointer flex-col items-center justify-center rounded-xl border border-dashed px-4 py-5 text-center transition-colors',
                          panFile ? 'border-[#C9A84C]/50 bg-[#C9A84C]/8' : 'border-white/20 bg-white/[0.03] hover:border-[#C9A84C]/35',
                        )}
                      >
                        <Upload className="mb-2 h-5 w-5 text-[#C9A84C]" />
                        {panFile ? (
                          <span className="flex w-full min-w-0 flex-col items-center gap-0.5 px-1 text-xs text-white/70">
                            <span>{t('regPanSelected')}</span>
                            <span className="w-full truncate text-[#C9A84C]/90" title={panFile.name}>
                              {panFile.name}
                            </span>
                          </span>
                        ) : (
                          <span className="text-xs text-white/70">{t('regPanHint')}</span>
                        )}
                        <input
                          type="file"
                          accept={KYC_ACCEPT}
                          className="hidden"
                          onChange={(e) => {
                            setPanFile(e.target.files?.[0] || null);
                            setErrors((p) => ({ ...p, pan: undefined }));
                          }}
                        />
                      </label>
                      {errors.pan && <p className="text-xs text-red-400">{errors.pan}</p>}
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-white/70">
                      {t('regPassword')}
                      <span className="ml-0.5 text-red-400">*</span>
                    </Label>
                    <div className="relative">
                      <Input
                        className="h-11 rounded-xl border-white/15 bg-[#0a1628]/80 pr-11 text-base text-white sm:text-sm"
                        type={showPassword ? 'text' : 'password'}
                        value={form.password}
                        onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))}
                      />
                      <button
                        type="button"
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#C9A84C]/80"
                        onClick={() => setShowPassword((v) => !v)}
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    {form.password && (
                      <div className="space-y-1">
                        <div className="h-1.5 overflow-hidden rounded-full bg-white/10">
                          <div
                            className="h-full rounded-full transition-all duration-300"
                            style={{ width: strengthWidth, backgroundColor: strengthColor }}
                          />
                        </div>
                        <p className="text-[11px] font-semibold" style={{ color: strengthColor }}>
                          {strengthLabel}
                        </p>
                      </div>
                    )}
                    {errors.password && <p className="text-xs text-red-400">{errors.password}</p>}
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-white/70">
                      {t('regConfirmPassword')}
                      <span className="ml-0.5 text-red-400">*</span>
                    </Label>
                    <div className="relative">
                      <Input
                        className="h-11 rounded-xl border-white/15 bg-[#0a1628]/80 pr-11 text-base text-white sm:text-sm"
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={form.confirmPassword}
                        onChange={(e) => setForm((p) => ({ ...p, confirmPassword: e.target.value }))}
                      />
                      <button
                        type="button"
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#C9A84C]/80"
                        onClick={() => setShowConfirmPassword((v) => !v)}
                      >
                        {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    {errors.confirmPassword && <p className="text-xs text-red-400">{errors.confirmPassword}</p>}
                  </div>
                </div>

                {submitError && (
                  <div className="mt-4 rounded-xl border border-red-400/35 bg-red-400/10 px-3 py-2 text-xs text-red-300">
                    {submitError}
                  </div>
                )}

                <label className="mt-6 flex cursor-pointer items-start gap-3 rounded-xl border border-white/10 bg-white/[0.03] px-3.5 py-3 text-left">
                  <input
                    type="checkbox"
                    className="mt-0.5 h-4 w-4 shrink-0 rounded border-white/30 accent-[#C9A84C]"
                    checked={termsAccepted}
                    onChange={(e) => {
                      setTermsAccepted(e.target.checked);
                      setSubmitError('');
                    }}
                  />
                  <span className="text-xs leading-relaxed text-white/70">
                    {t('termsAccept') || tBase('termsAccept')}{' '}
                    <Link
                      to="/terms"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-semibold text-[#C9A84C] underline-offset-2 hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {t('termsLink') || tBase('termsLink')}
                    </Link>
                    {' · '}
                    <Link
                      to="/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-semibold text-[#C9A84C] underline-offset-2 hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Privacy Policy
                    </Link>
                  </span>
                </label>

                <label className="mt-3 flex cursor-pointer items-start gap-3 rounded-xl border border-white/10 bg-white/[0.03] px-3.5 py-3 text-left">
                  <input
                    type="checkbox"
                    className="mt-0.5 h-4 w-4 shrink-0 rounded border-white/30 accent-[#C9A84C]"
                    checked={dpdpAccepted}
                    onChange={(e) => {
                      setDpdpAccepted(e.target.checked);
                      setSubmitError('');
                    }}
                  />
                  <span className="text-xs leading-relaxed text-white/70">
                    {t('regDpdpAccept')}
                  </span>
                </label>

                <div
                  className="mt-3 rounded-xl px-3.5 py-3"
                  style={{
                    background: 'linear-gradient(135deg, #0f2040, #0a1628)',
                    border: '1px solid rgba(201,168,76,0.4)',
                  }}
                >
                  <button
                    type="button"
                    onClick={() => setDpdpOpen((open) => !open)}
                    className="flex w-full cursor-pointer items-center justify-between gap-3 bg-transparent p-0 text-left"
                    style={{ outline: 'none' }}
                    aria-expanded={dpdpOpen}
                  >
                    <span className="text-xs font-semibold leading-relaxed text-[#C9A84C]">
                      {t('regDpdpTitle')}
                    </span>
                    <ChevronDown
                      className={cn(
                        'h-4 w-4 shrink-0 text-[#C9A84C] transition-transform',
                        dpdpOpen && 'rotate-180',
                      )}
                    />
                  </button>
                  {dpdpOpen && (
                    <p className="mt-2 text-xs leading-relaxed text-white/70">
                      {t('regDpdpBody')}
                    </p>
                  )}
                </div>

                <Button
                  type="button"
                  disabled={loading || !termsAccepted || !dpdpAccepted}
                  className="mt-4 h-12 w-full rounded-2xl border-0 text-sm font-extrabold text-[#0D1B2A] disabled:opacity-60"
                  style={{ background: `linear-gradient(90deg, #a07830, ${GOLD}, #a07830)` }}
                  onClick={handleSubmit}
                >
                  {loading ? t('regSubmitting') : t('regSubmit')}
                </Button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <p className="mt-4 px-1 text-center text-xs leading-relaxed text-white/45">
          {t('regNoInviteCode')}{' '}
          <a
            href={WHATSAPP_INVITE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold text-[#C9A84C] hover:underline"
          >
            WhatsApp
          </a>
        </p>

        {isApproved && (
          <p className="mt-6 text-center text-xs text-white/30">
            <Link
              to={loginPath}
              className="font-semibold text-[#C9A84C] hover:underline"
            >
              {tBase('marketingNavLogin')}
            </Link>
          </p>
        )}
      </main>
    </div>
  );
};

export function ClientRegisterPage() {
  return <RegisterPage fixedRole="client" />;
}

export function OwnerRegisterPage() {
  return <RegisterPage fixedRole="owner" />;
}

export default RegisterPage;
